import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { useAppConfig } from '../context/AppConfigContext';
import { isAdminEmail } from '../utils/admin';

export default function SecretPen() {
  const { user, loading } = useAuth();
  const { adminEmails } = useAppConfig();
  const isAdmin = useMemo(
    () => isAdminEmail(user?.email, adminEmails),
    [user?.email, adminEmails]
  );
  const [activeGame, setActiveGame] = useState('drift');

  if (loading) {
    return <div style={{ paddingTop: '4rem', textAlign: 'center' }}>Loading…</div>;
  }

  if (!isAdmin) {
    return (
      <div className="page-section">
        <div className="page-container" style={{ maxWidth: 1100 }}>
          <div className="glass-card" style={{ padding: 18, textAlign: 'center' }}>
            Not Found
          </div>
        </div>
      </div>
    );
  }

  const gameTabs = [
    { id: 'drift', label: 'Neon Drift' },
    { id: 'hopper', label: 'Orbit Hopper' },
    { id: 'fps', label: 'Neon FPS' },
    { id: 'emu', label: 'PS1 Emulator' },
  ];

  return (
    <div className="page-section">
      <div className="page-container" style={{ maxWidth: 1100 }}>
        <h1 className="title" style={{ textAlign: 'center', marginTop: 0 }}>Secret Arcade</h1>
        <p className="subtitle" style={{ textAlign: 'center' }}>
          Three different games. One is a full FPS shooter.
        </p>

        <div style={{ display: 'flex', justifyContent: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 12 }}>
          {gameTabs.map((tab) => (
            <button
              key={tab.id}
              type="button"
              className={activeGame === tab.id ? 'btn-glass-primary' : 'btn-glass-secondary'}
              onClick={() => setActiveGame(tab.id)}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {activeGame === 'drift' ? <NeonDriftGame /> : null}
        {activeGame === 'hopper' ? <OrbitHopperGame /> : null}
        {activeGame === 'fps' ? <NeonFPSGame /> : null}
        {activeGame === 'emu' ? <EmulatorHub /> : null}
      </div>
    </div>
  );
}



function GameShell({ title, subtitle, children }) {
  return (
    <div className="glass-card" style={{ padding: 16 }}>
      <h2 style={{ marginTop: 0, textAlign: 'center' }}>{title}</h2>
      <div className="meta-text" style={{ textAlign: 'center', marginBottom: 12 }}>{subtitle}</div>
      {children}
    </div>
  );
}

function EmulatorHub() {
  const [psxOk, setPsxOk] = useState(null);

  useEffect(() => {
    let alive = true;
    const check = async () => {
      try {
        const res = await fetch('/emulators/psx/index.html', { cache: 'no-cache' });
        if (!alive) return;
        setPsxOk(Boolean(res.ok));
      } catch (e) {
        if (!alive) return;
        setPsxOk(false);
      }
    };
    check();
    return () => {
      alive = false;
    };
  }, []);

  return (
    <GameShell
      title="PS1 Emulator"
      subtitle="EmulatorJS powered PS1. Local BIOS + local game file picker (no ROM links)."
    >
      {psxOk !== true ? (
        <div className="meta-text" style={{ marginBottom: 12, textAlign: 'center' }}>
          {psxOk === null ? 'Checking emulator…' : 'PS1 emulator page not found.'}
        </div>
      ) : null}

      <div style={{ display: 'grid', gap: 12 }}>
        <div className="meta-text" style={{ opacity: 0.9 }}>
          Pick your PS1 BIOS and game file inside the emulator frame, then press Start.
        </div>

        <div
          style={{
            position: 'relative',
            width: '100%',
            height: '70vh',
            minHeight: 520,
            borderRadius: 16,
            overflow: 'hidden',
            border: '1px solid rgba(120,140,255,0.25)',
            background: 'rgba(10,12,24,0.8)',
            boxShadow: '0 20px 80px rgba(0,0,0,0.55), inset 0 0 40px rgba(120,140,255,0.10)',
          }}
        >
          <iframe
            title="PS1 Emulator"
            src="/emulators/psx/index.html"
            style={{ width: '100%', height: '100%', border: 0, display: 'block' }}
            sandbox="allow-scripts allow-same-origin allow-pointer-lock allow-forms allow-fullscreen"
            allow="fullscreen; gamepad; pointer-lock"
            allowFullScreen
          />
        </div>
      </div>
    </GameShell>
  );
}

function NeonDriftGame() {
  const canvasRef = useRef(null);
  const wrapRef = useRef(null);
  const rafRef = useRef(null);
  const lastTimeRef = useRef(0);
  const pointerRef = useRef({ x: 0.5, y: 0.6, active: false });
  const keysRef = useRef({ left: false, right: false, up: false, down: false });
  const runningRef = useRef(false);
  const gameRef = useRef({
    ship: { x: 0, y: 0, vx: 0, vy: 0 },
    stars: [],
    enemies: [],
    orbs: [],
    particles: [],
    score: 0,
    lives: 3,
    t: 0,
    spawnEnemyAt: 0,
    spawnOrbAt: 0,
  });

  const [score, setScore] = useState(0);
  const [lives, setLives] = useState(3);
  const [gameOver, setGameOver] = useState(false);
  const [isRunning, setIsRunning] = useState(false);
  const [bestScore, setBestScore] = useState(() => {
    try {
      const v = localStorage.getItem('SECRET_BEST_DRIFT');
      return v ? Number(v) || 0 : 0;
    } catch (e) {
      return 0;
    }
  });

  const resetGame = () => {
    const g = gameRef.current;
    g.ship = { x: 0, y: 0, vx: 0, vy: 0 };
    g.stars = [];
    g.enemies = [];
    g.orbs = [];
    g.particles = [];
    g.score = 0;
    g.lives = 3;
    g.t = 0;
    g.spawnEnemyAt = 0;
    g.spawnOrbAt = 0;
    setScore(0);
    setLives(3);
    setGameOver(false);
  };

  const startGame = () => {
    resetGame();
    runningRef.current = true;
    setIsRunning(true);
    lastTimeRef.current = performance.now();
    rafRef.current = requestAnimationFrame(tick);
  };

  const stopGame = () => {
    runningRef.current = false;
    setIsRunning(false);
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
  };

  const spawnStarfield = (count, w, h) => {
    const stars = [];
    for (let i = 0; i < count; i += 1) {
      stars.push({
        x: Math.random() * w,
        y: Math.random() * h,
        r: Math.random() * 1.6 + 0.4,
        s: Math.random() * 40 + 20,
        a: Math.random() * 0.6 + 0.2,
      });
    }
    return stars;
  };

  const emitParticles = (x, y, color, count = 18) => {
    const g = gameRef.current;
    for (let i = 0; i < count; i += 1) {
      const ang = Math.random() * Math.PI * 2;
      const spd = Math.random() * 220 + 60;
      g.particles.push({
        x,
        y,
        vx: Math.cos(ang) * spd,
        vy: Math.sin(ang) * spd,
        r: Math.random() * 3 + 1.5,
        life: Math.random() * 0.6 + 0.4,
        color,
      });
    }
  };

  const tick = (now) => {
    if (!runningRef.current) return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    const w = canvas.width;
    const h = canvas.height;
    const dt = Math.min(0.05, (now - lastTimeRef.current) / 1000);
    lastTimeRef.current = now;
    const g = gameRef.current;
    g.t += dt;

    if (!g.stars.length) {
      g.stars = spawnStarfield(Math.floor((w * h) / 6000), w, h);
    }

    const bg = ctx.createLinearGradient(0, 0, 0, h);
    bg.addColorStop(0, '#05060f');
    bg.addColorStop(0.5, '#0b0f2b');
    bg.addColorStop(1, '#04050a');
    ctx.fillStyle = bg;
    ctx.fillRect(0, 0, w, h);

    for (const s of g.stars) {
      s.y += s.s * dt;
      if (s.y > h + 20) {
        s.y = -20;
        s.x = Math.random() * w;
      }
      ctx.beginPath();
      ctx.fillStyle = `rgba(200,220,255,${s.a})`;
      ctx.arc(s.x, s.y, s.r, 0, Math.PI * 2);
      ctx.fill();
    }

    const baseX = pointerRef.current.active ? pointerRef.current.x * w : w * 0.5;
    const baseY = pointerRef.current.active ? pointerRef.current.y * h : h * 0.65;
    const keyX = (keysRef.current.right ? 1 : 0) - (keysRef.current.left ? 1 : 0);
    const keyY = (keysRef.current.down ? 1 : 0) - (keysRef.current.up ? 1 : 0);
    const targetX = Math.max(0, Math.min(w, baseX + keyX * w * 0.22));
    const targetY = Math.max(0, Math.min(h, baseY + keyY * h * 0.18));
    const ship = g.ship;
    ship.vx += (targetX - ship.x) * 4.5 * dt;
    ship.vy += (targetY - ship.y) * 4.5 * dt;
    ship.vx *= 0.9;
    ship.vy *= 0.9;
    ship.x += ship.vx;
    ship.y += ship.vy;
    if (!ship.x && !ship.y) {
      ship.x = targetX;
      ship.y = targetY;
    }

    const difficulty = 1 + Math.min(2, g.score / 20);

    if (g.t > g.spawnEnemyAt) {
      g.spawnEnemyAt = g.t + (0.65 - Math.min(0.35, g.score * 0.01));
      g.enemies.push({
        x: Math.random() * (w - 80) + 40,
        y: -30,
        r: Math.random() * 18 + 16,
        vy: Math.random() * 140 + 120 * difficulty,
        vx: (Math.random() - 0.5) * 60,
        spin: (Math.random() - 0.5) * 3,
        a: Math.random() * Math.PI * 2,
      });
    }

    if (g.t > g.spawnOrbAt) {
      g.spawnOrbAt = g.t + 1.1 + Math.random() * 0.6;
      g.orbs.push({
        x: Math.random() * (w - 120) + 60,
        y: -20,
        r: 10,
        vy: Math.random() * 120 + 90,
      });
    }

    for (let i = g.enemies.length - 1; i >= 0; i -= 1) {
      const e = g.enemies[i];
      e.y += e.vy * dt;
      e.x += e.vx * dt;
      e.a += e.spin * dt;
      if (e.y > h + 60) {
        g.enemies.splice(i, 1);
        continue;
      }
      const dx = ship.x - e.x;
      const dy = ship.y - e.y;
      const dist = Math.hypot(dx, dy);
      if (dist < e.r + 18) {
        g.enemies.splice(i, 1);
        g.lives -= 1;
        setLives(g.lives);
        emitParticles(e.x, e.y, '#ff3b5f');
        if (g.lives <= 0) {
          setGameOver(true);
          setIsRunning(false);
          if (g.score > bestScore) {
            setBestScore(g.score);
            try { localStorage.setItem('SECRET_BEST_DRIFT', String(g.score)); } catch (e) {}
          }
          stopGame();
        }
      }
    }

    for (let i = g.orbs.length - 1; i >= 0; i -= 1) {
      const o = g.orbs[i];
      o.y += o.vy * dt;
      if (o.y > h + 30) {
        g.orbs.splice(i, 1);
        continue;
      }
      const dx = ship.x - o.x;
      const dy = ship.y - o.y;
      if (Math.hypot(dx, dy) < 24) {
        g.orbs.splice(i, 1);
        g.score += 1;
        setScore(g.score);
        emitParticles(o.x, o.y, '#38f2ff', 12);
      }
    }

    for (let i = g.particles.length - 1; i >= 0; i -= 1) {
      const p = g.particles[i];
      p.life -= dt;
      if (p.life <= 0) {
        g.particles.splice(i, 1);
        continue;
      }
      p.x += p.vx * dt;
      p.y += p.vy * dt;
      p.vx *= 0.97;
      p.vy *= 0.97;
    }

    for (const o of g.orbs) {
      ctx.save();
      ctx.shadowColor = '#38f2ff';
      ctx.shadowBlur = 20;
      ctx.fillStyle = '#7df6ff';
      ctx.beginPath();
      ctx.arc(o.x, o.y, o.r, 0, Math.PI * 2);
      ctx.fill();
      ctx.restore();
    }

    for (const e of g.enemies) {
      ctx.save();
      ctx.translate(e.x, e.y);
      ctx.rotate(e.a);
      ctx.shadowColor = '#ff3b5f';
      ctx.shadowBlur = 24;
      ctx.fillStyle = '#ff2d4f';
      ctx.beginPath();
      for (let i = 0; i < 6; i += 1) {
        const ang = (Math.PI * 2 * i) / 6;
        const rad = i % 2 === 0 ? e.r : e.r * 0.6;
        const px = Math.cos(ang) * rad;
        const py = Math.sin(ang) * rad;
        if (i === 0) ctx.moveTo(px, py); else ctx.lineTo(px, py);
      }
      ctx.closePath();
      ctx.fill();
      ctx.restore();
    }

    ctx.save();
    ctx.translate(ship.x, ship.y);
    ctx.shadowColor = '#7d7bff';
    ctx.shadowBlur = 28;
    const grad = ctx.createRadialGradient(0, 0, 4, 0, 0, 28);
    grad.addColorStop(0, '#c6d1ff');
    grad.addColorStop(0.5, '#6b7dff');
    grad.addColorStop(1, '#2d2b5f');
    ctx.fillStyle = grad;
    ctx.beginPath();
    ctx.moveTo(0, -22);
    ctx.lineTo(18, 18);
    ctx.lineTo(0, 12);
    ctx.lineTo(-18, 18);
    ctx.closePath();
    ctx.fill();
    ctx.restore();

    for (const p of g.particles) {
      ctx.save();
      ctx.globalAlpha = Math.max(0, Math.min(1, p.life));
      ctx.shadowColor = p.color;
      ctx.shadowBlur = 16;
      ctx.fillStyle = p.color;
      ctx.beginPath();
      ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2);
      ctx.fill();
      ctx.restore();
    }

    rafRef.current = requestAnimationFrame(tick);
  };

  useEffect(() => {
    const resize = () => {
      const canvas = canvasRef.current;
      const wrap = wrapRef.current;
      if (!canvas || !wrap) return;
      const rect = wrap.getBoundingClientRect();
      const dpr = Math.min(2, window.devicePixelRatio || 1);
      canvas.width = rect.width * dpr;
      canvas.height = rect.height * dpr;
      canvas.style.width = `${rect.width}px`;
      canvas.style.height = `${rect.height}px`;
      const ctx = canvas.getContext('2d');
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      gameRef.current.stars = [];
    };
    resize();
    window.addEventListener('resize', resize);
    return () => window.removeEventListener('resize', resize);
  }, []);

  useEffect(() => {
    const wrap = wrapRef.current;
    if (!wrap) return () => {};
    const onMove = (e) => {
      const rect = wrap.getBoundingClientRect();
      pointerRef.current = {
        x: (e.clientX - rect.left) / rect.width,
        y: (e.clientY - rect.top) / rect.height,
        active: true,
      };
    };
    const onLeave = () => {
      pointerRef.current.active = false;
    };
    wrap.addEventListener('pointermove', onMove);
    wrap.addEventListener('pointerleave', onLeave);
    return () => {
      wrap.removeEventListener('pointermove', onMove);
      wrap.removeEventListener('pointerleave', onLeave);
    };
  }, []);

  useEffect(() => {
    const onKeyDown = (e) => {
      if (e.code === 'ArrowLeft' || e.code === 'KeyA') keysRef.current.left = true;
      if (e.code === 'ArrowRight' || e.code === 'KeyD') keysRef.current.right = true;
      if (e.code === 'ArrowUp' || e.code === 'KeyW') keysRef.current.up = true;
      if (e.code === 'ArrowDown' || e.code === 'KeyS') keysRef.current.down = true;
      if (e.code === 'ArrowLeft' || e.code === 'ArrowRight' || e.code === 'ArrowUp' || e.code === 'ArrowDown') {
        e.preventDefault();
      }
    };
    const onKeyUp = (e) => {
      if (e.code === 'ArrowLeft' || e.code === 'KeyA') keysRef.current.left = false;
      if (e.code === 'ArrowRight' || e.code === 'KeyD') keysRef.current.right = false;
      if (e.code === 'ArrowUp' || e.code === 'KeyW') keysRef.current.up = false;
      if (e.code === 'ArrowDown' || e.code === 'KeyS') keysRef.current.down = false;
    };
    window.addEventListener('keydown', onKeyDown, { passive: false });
    window.addEventListener('keyup', onKeyUp);
    return () => {
      window.removeEventListener('keydown', onKeyDown);
      window.removeEventListener('keyup', onKeyUp);
    };
  }, []);

  useEffect(() => {
    return () => {
      runningRef.current = false;
      setIsRunning(false);
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, []);

  return (
    <GameShell
      title="Neon Drift"
      subtitle="Collect cyan orbs. Dodge red rifts. Mouse/touch to steer — or use WASD/arrow keys."
    >
      <GameCanvas
        wrapRef={wrapRef}
        canvasRef={canvasRef}
        score={score}
        lives={lives}
        bestScore={bestScore}
        gameOver={gameOver}
        isRunning={isRunning}
        onStart={startGame}
        onPause={stopGame}
        controls={(
          <div>
            <div><strong>Move:</strong> mouse/touch, or <strong>WASD / arrow keys</strong></div>
            <div><strong>Goal:</strong> collect cyan orbs, avoid red rifts</div>
            <div><strong>Fullscreen:</strong> use the Fullscreen button (Esc exits)</div>
          </div>
        )}
      />
    </GameShell>
  );
}

function OrbitHopperGame() {
  const canvasRef = useRef(null);
  const wrapRef = useRef(null);
  const rafRef = useRef(null);
  const lastRef = useRef(0);
  const keysRef = useRef({ jump: false });
  const runningRef = useRef(false);
  const gameRef = useRef({
    player: { x: 160, y: 0, vy: 0, r: 16, onGround: false },
    platforms: [],
    stars: [],
    score: 0,
    t: 0,
  });

  const [score, setScore] = useState(0);
  const [gameOver, setGameOver] = useState(false);
  const [isRunning, setIsRunning] = useState(false);
  const [bestScore, setBestScore] = useState(() => {
    try {
      const v = localStorage.getItem('SECRET_BEST_HOPPER');
      return v ? Number(v) || 0 : 0;
    } catch (e) {
      return 0;
    }
  });

  const resetGame = (w, h) => {
    const g = gameRef.current;
    g.player = { x: 160, y: h * 0.5, vy: 0, r: 16, onGround: false };
    g.platforms = [];
    g.stars = [];
    g.score = 0;
    g.t = 0;
    const baseY = h * 0.7;
    for (let i = 0; i < 6; i += 1) {
      g.platforms.push({
        x: i * 180,
        y: baseY - (i % 2) * 60,
        w: 140,
        h: 16,
      });
      g.stars.push({
        x: i * 180 + 70,
        y: baseY - (i % 2) * 60 - 40,
        r: 8,
        active: true,
      });
    }
    setScore(0);
    setGameOver(false);
  };

  const startGame = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    resetGame(canvas.width, canvas.height);
    runningRef.current = true;
    setIsRunning(true);
    lastRef.current = performance.now();
    rafRef.current = requestAnimationFrame(tick);
  };

  const stopGame = () => {
    runningRef.current = false;
    setIsRunning(false);
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
  };

  const tick = (now) => {
    if (!runningRef.current) return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    const w = canvas.width;
    const h = canvas.height;
    const dt = Math.min(0.05, (now - lastRef.current) / 1000);
    lastRef.current = now;
    const g = gameRef.current;

    const bg = ctx.createLinearGradient(0, 0, 0, h);
    bg.addColorStop(0, '#0a0b16');
    bg.addColorStop(1, '#06070f');
    ctx.fillStyle = bg;
    ctx.fillRect(0, 0, w, h);

    const speed = 220 + g.score * 4;
    const gravity = 1200;
    const player = g.player;

    player.vy += gravity * dt;
    player.y += player.vy * dt;
    player.onGround = false;

    for (const p of g.platforms) {
      p.x -= speed * dt;
    }
    for (const s of g.stars) {
      s.x -= speed * dt;
    }

    const lastPlatform = g.platforms[g.platforms.length - 1];
    if (lastPlatform && lastPlatform.x < w - 180) {
      const newY = (Math.random() * 0.4 + 0.4) * h;
      const newX = w + Math.random() * 120;
      g.platforms.push({ x: newX, y: newY, w: 150, h: 16 });
      g.stars.push({ x: newX + 80, y: newY - 40, r: 8, active: true });
    }

    while (g.platforms.length && g.platforms[0].x + g.platforms[0].w < -60) {
      g.platforms.shift();
    }
    while (g.stars.length && g.stars[0].x < -60) {
      g.stars.shift();
    }

    for (const p of g.platforms) {
      const withinX = player.x + player.r > p.x && player.x - player.r < p.x + p.w;
      const falling = player.vy > 0;
      if (withinX && falling && player.y + player.r >= p.y && player.y + player.r <= p.y + p.h + 12) {
        player.y = p.y - player.r;
        player.vy = 0;
        player.onGround = true;
      }
      ctx.fillStyle = 'rgba(120,150,255,0.3)';
      ctx.fillRect(p.x, p.y, p.w, p.h);
      ctx.fillStyle = 'rgba(120,150,255,0.8)';
      ctx.fillRect(p.x, p.y, p.w, 3);
    }

    if ((keysRef.current.jump || player.onGround) && player.onGround) {
      player.vy = -520;
      player.onGround = false;
      keysRef.current.jump = false;
    }

    for (const s of g.stars) {
      if (!s.active) continue;
      const dx = player.x - s.x;
      const dy = player.y - s.y;
      if (Math.hypot(dx, dy) < player.r + s.r + 6) {
        s.active = false;
        g.score += 1;
        setScore(g.score);
      }
      if (s.active) {
        ctx.save();
        ctx.shadowColor = '#ffe66d';
        ctx.shadowBlur = 18;
        ctx.fillStyle = '#fff3b0';
        ctx.beginPath();
        ctx.arc(s.x, s.y, s.r, 0, Math.PI * 2);
        ctx.fill();
        ctx.restore();
      }
    }

    ctx.save();
    ctx.shadowColor = '#8ef6ff';
    ctx.shadowBlur = 18;
    ctx.fillStyle = '#5de2ff';
    ctx.beginPath();
    ctx.arc(player.x, player.y, player.r, 0, Math.PI * 2);
    ctx.fill();
    ctx.restore();

    g.t += dt;
    if (player.y > h + 80) {
      setGameOver(true);
      setIsRunning(false);
      if (g.score > bestScore) {
        setBestScore(g.score);
        try { localStorage.setItem('SECRET_BEST_HOPPER', String(g.score)); } catch (e) {}
      }
      stopGame();
    }

    rafRef.current = requestAnimationFrame(tick);
  };

  useEffect(() => {
    const resize = () => {
      const canvas = canvasRef.current;
      const wrap = wrapRef.current;
      if (!canvas || !wrap) return;
      const rect = wrap.getBoundingClientRect();
      const dpr = Math.min(2, window.devicePixelRatio || 1);
      canvas.width = rect.width * dpr;
      canvas.height = rect.height * dpr;
      canvas.style.width = `${rect.width}px`;
      canvas.style.height = `${rect.height}px`;
      const ctx = canvas.getContext('2d');
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };
    resize();
    window.addEventListener('resize', resize);
    return () => window.removeEventListener('resize', resize);
  }, []);

  useEffect(() => {
    const onKeyDown = (e) => {
      if (e.code === 'Space' || e.code === 'ArrowUp' || e.code === 'KeyW') {
        keysRef.current.jump = true;
        e.preventDefault();
      }
    };
    window.addEventListener('keydown', onKeyDown, { passive: false });
    return () => window.removeEventListener('keydown', onKeyDown);
  }, []);

  useEffect(() => {
    return () => {
      runningRef.current = false;
      setIsRunning(false);
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, []);

  return (
    <GameShell
      title="Orbit Hopper"
      subtitle="Auto-runner. Tap Space or click to jump and collect stars."
    >
      <GameCanvas
        wrapRef={wrapRef}
        canvasRef={canvasRef}
        score={score}
        lives={null}
        bestScore={bestScore}
        gameOver={gameOver}
        isRunning={isRunning}
        onStart={startGame}
        onPause={stopGame}
        onClick={() => { keysRef.current.jump = true; }}
        controls={(
          <div>
            <div><strong>Jump:</strong> Space / W / ↑ or click/tap</div>
            <div><strong>Goal:</strong> collect stars, don’t fall</div>
            <div><strong>Fullscreen:</strong> use the Fullscreen button (Esc exits)</div>
          </div>
        )}
      />
    </GameShell>
  );
}

function NeonFPSGame() {
  const canvasRef = useRef(null);
  const wrapRef = useRef(null);
  const rafRef = useRef(null);
  const lastRef = useRef(0);
  const keysRef = useRef({});
  const runningRef = useRef(false);
  const pointerLockedRef = useRef(false);
  const inputRef = useRef({
    shoot: false,
    reload: false,
  });
  const shootHoldRef = useRef(null);
  const gameRef = useRef({
    x: 3.5,
    y: 3.5,
    dir: 0,
    pitch: 0,
    score: 0,
    t: 0,
    hp: 100,
    level: 1,
    kills: 0,
    killsNeeded: 8,
    map: [],
    mapW: 16,
    mapH: 16,
    enemies: [],
    spawnAt: 0,
    fireCooldown: 0,
    ammo: 12,
    reserve: 60,
    reloading: 0,
    muzzle: 0,
    recoil: 0,
    hitMarker: 0,
    bob: 0,
    bobT: 0,
    levelBanner: 0,
    shake: 0,
  });

  const [score, setScore] = useState(0);
  const [isRunning, setIsRunning] = useState(false);
  const [hp, setHp] = useState(100);
  const [ammo, setAmmo] = useState(12);
  const [reserve, setReserve] = useState(60);
  const [level, setLevel] = useState(1);
  const [kills, setKills] = useState(0);
  const [killsNeeded, setKillsNeeded] = useState(8);
  const [gameOver, setGameOver] = useState(false);

  const levels = useMemo(() => ([
    {
      level: 1,
      density: 0.13,
      maxEnemies: 3,
      spawnEvery: 2.4,
      enemySpeed: 0.7,
      enemyHp: 1,
      killsNeeded: 8,
      fov: Math.PI / 3,
    },
    {
      level: 2,
      density: 0.16,
      maxEnemies: 4,
      spawnEvery: 2.0,
      enemySpeed: 0.8,
      enemyHp: 1,
      killsNeeded: 10,
      fov: Math.PI / 3,
    },
    {
      level: 3,
      density: 0.20,
      maxEnemies: 5,
      spawnEvery: 1.7,
      enemySpeed: 0.9,
      enemyHp: 2,
      killsNeeded: 12,
      fov: Math.PI / 2.9,
    },
    {
      level: 4,
      density: 0.24,
      maxEnemies: 6,
      spawnEvery: 1.45,
      enemySpeed: 1.05,
      enemyHp: 2,
      killsNeeded: 14,
      fov: Math.PI / 2.8,
    },
    {
      level: 5,
      density: 0.28,
      maxEnemies: 7,
      spawnEvery: 1.25,
      enemySpeed: 1.15,
      enemyHp: 3,
      killsNeeded: 16,
      fov: Math.PI / 2.75,
    },
  ]), []);

  const clamp = (v, a, b) => Math.max(a, Math.min(b, v));
  const wrapAngle = (a) => {
    let x = a;
    while (x <= -Math.PI) x += Math.PI * 2;
    while (x > Math.PI) x -= Math.PI * 2;
    return x;
  };

  const buildMap = (cfg) => {
    const w = 16;
    const h = 16;
    const grid = Array.from({ length: h }, () => new Array(w).fill(0));

    for (let y = 0; y < h; y += 1) {
      for (let x = 0; x < w; x += 1) {
        if (x === 0 || y === 0 || x === w - 1 || y === h - 1) {
          grid[y][x] = 1;
        } else {
          grid[y][x] = Math.random() < cfg.density ? 1 : 0;
        }
      }
    }

    // Clear spawn zone.
    for (let y = 2; y <= 5; y += 1) {
      for (let x = 2; x <= 5; x += 1) {
        grid[y][x] = 0;
      }
    }

    // Add a few guaranteed corridors.
    for (let x = 1; x < w - 1; x += 1) grid[8][x] = 0;
    for (let y = 1; y < h - 1; y += 1) grid[y][8] = 0;

    return grid.map((row) => row.map((c) => (c ? '1' : '0')).join(''));
  };

  const getCfg = () => {
    const g = gameRef.current;
    return levels[Math.min(levels.length - 1, Math.max(0, (g.level || 1) - 1))];
  };

  const isWall = (x, y) => {
    const g = gameRef.current;
    const mx = Math.floor(x);
    const my = Math.floor(y);
    if (mx < 0 || my < 0 || mx >= g.mapW || my >= g.mapH) return true;
    return g.map[my][mx] === '1';
  };

  const castWall = (px, py, ang, maxSteps = 256) => {
    const g = gameRef.current;
    const rayDirX = Math.cos(ang);
    const rayDirY = Math.sin(ang);

    let mapX = Math.floor(px);
    let mapY = Math.floor(py);

    const deltaDistX = Math.abs(1 / (rayDirX || 0.0001));
    const deltaDistY = Math.abs(1 / (rayDirY || 0.0001));

    let stepX = 0;
    let stepY = 0;
    let sideDistX = 0;
    let sideDistY = 0;
    let side = 0;

    if (rayDirX < 0) {
      stepX = -1;
      sideDistX = (px - mapX) * deltaDistX;
    } else {
      stepX = 1;
      sideDistX = (mapX + 1.0 - px) * deltaDistX;
    }

    if (rayDirY < 0) {
      stepY = -1;
      sideDistY = (py - mapY) * deltaDistY;
    } else {
      stepY = 1;
      sideDistY = (mapY + 1.0 - py) * deltaDistY;
    }

    let hit = false;
    let safety = 0;
    while (!hit && safety < maxSteps) {
      safety += 1;
      if (sideDistX < sideDistY) {
        sideDistX += deltaDistX;
        mapX += stepX;
        side = 0;
      } else {
        sideDistY += deltaDistY;
        mapY += stepY;
        side = 1;
      }
      if (mapY < 0 || mapY >= g.mapH || mapX < 0 || mapX >= g.mapW) {
        hit = true;
        break;
      }
      if (g.map[mapY][mapX] === '1') hit = true;
    }

    const dist = side === 0 ? (sideDistX - deltaDistX) : (sideDistY - deltaDistY);
    return { dist: Math.max(0.001, dist), side };
  };

  const spawnEnemy = () => {
    const g = gameRef.current;
    const cfg = getCfg();
    const triesMax = 120;
    for (let t = 0; t < triesMax; t += 1) {
      const ex = 1.5 + Math.random() * (g.mapW - 3);
      const ey = 1.5 + Math.random() * (g.mapH - 3);
      if (isWall(ex, ey)) continue;
      const dist = Math.hypot(ex - g.x, ey - g.y);
      if (dist < 4.2) continue;
      g.enemies.push({
        x: ex,
        y: ey,
        hp: cfg.enemyHp,
        spd: cfg.enemySpeed * (0.85 + Math.random() * 0.35),
        t: Math.random() * 10,
        bite: 0,
      });
      return;
    }
  };

  const resetGame = () => {
    const g = gameRef.current;
    g.x = 3.5;
    g.y = 3.5;
    g.dir = 0;
    g.pitch = 0;
    g.score = 0;
    g.t = 0;
    g.hp = 100;
    g.level = 1;
    g.kills = 0;
    g.fireCooldown = 0;
    g.ammo = 12;
    g.reserve = 60;
    g.reloading = 0;
    g.muzzle = 0;
    g.recoil = 0;
    g.hitMarker = 0;
    g.bob = 0;
    g.bobT = 0;
    g.levelBanner = 1.2;
    g.shake = 0;
    const cfg = levels[0];
    g.killsNeeded = cfg.killsNeeded;
    g.mapW = 16;
    g.mapH = 16;
    g.map = buildMap(cfg);
    g.enemies = [];
    g.spawnAt = 0.8;
    setScore(0);
    setHp(100);
    setAmmo(12);
    setReserve(60);
    setLevel(1);
    setKills(0);
    setKillsNeeded(cfg.killsNeeded);
    setGameOver(false);
  };

  const startLevel = (nextLevel) => {
    const g = gameRef.current;
    const idx = Math.min(levels.length - 1, Math.max(0, nextLevel - 1));
    const cfg = levels[idx];
    g.level = cfg.level;
    g.kills = 0;
    g.killsNeeded = cfg.killsNeeded;
    g.x = 3.5;
    g.y = 3.5;
    g.dir = 0;
    g.pitch = 0;
    g.map = buildMap(cfg);
    g.enemies = [];
    g.spawnAt = g.t + 0.8;
    g.levelBanner = 1.15;
    setLevel(cfg.level);
    setKills(0);
    setKillsNeeded(cfg.killsNeeded);
  };

  const startGame = () => {
    resetGame();
    runningRef.current = true;
    setIsRunning(true);
    lastRef.current = performance.now();
    rafRef.current = requestAnimationFrame(tick);
  };

  const stopGame = () => {
    runningRef.current = false;
    setIsRunning(false);
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
  };

  const tick = (now) => {
    if (!runningRef.current) return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    const dpr = Math.min(2, window.devicePixelRatio || 1);
    const w = canvas.width / dpr;
    const h = canvas.height / dpr;
    const dt = Math.min(0.05, (now - lastRef.current) / 1000);
    lastRef.current = now;
    const g = gameRef.current;

    if (gameOver) {
      return;
    }

    const cfg = getCfg();

    g.fireCooldown = Math.max(0, g.fireCooldown - dt);
    g.reloading = Math.max(0, g.reloading - dt);
    g.muzzle = Math.max(0, g.muzzle - dt);
    g.recoil = Math.max(0, g.recoil - dt * 2.2);
    g.hitMarker = Math.max(0, g.hitMarker - dt);
    g.levelBanner = Math.max(0, g.levelBanner - dt);
    g.shake = Math.max(0, g.shake - dt * 3);

    const moveSpeed = 2.5;
    const rotSpeed = 2.2;
    const keys = keysRef.current;
    const moveF = (keys.KeyW ? 1 : 0) - (keys.KeyS ? 1 : 0);
    const moveS = (keys.KeyE ? 1 : 0) - (keys.KeyQ ? 1 : 0);
    const turning = (keys.ArrowRight || keys.KeyD ? 1 : 0) - (keys.ArrowLeft || keys.KeyA ? 1 : 0);

    g.dir += turning * rotSpeed * dt;

    const dx = Math.cos(g.dir);
    const dy = Math.sin(g.dir);
    const sx = -dy;
    const sy = dx;

    const wantsMove = Math.abs(moveF) + Math.abs(moveS) > 0.01;
    const bobTarget = wantsMove ? 1 : 0;
    g.bob = g.bob * 0.9 + bobTarget * 0.1;
    g.bobT += dt * (wantsMove ? 10 : 4);

    const nx = g.x + (dx * moveF * moveSpeed + sx * moveS * moveSpeed) * dt;
    const ny = g.y + (dy * moveF * moveSpeed + sy * moveS * moveSpeed) * dt;
    if (!isWall(nx, g.y)) g.x = nx;
    if (!isWall(g.x, ny)) g.y = ny;

    // Reload request.
    if (inputRef.current.reload || keys.KeyR) {
      inputRef.current.reload = false;
      if (g.reloading <= 0 && g.ammo < 12 && g.reserve > 0) {
        g.reloading = 0.9;
      }
    }

    if (g.reloading > 0) {
      // Finish reload when timer expires.
      if (g.reloading <= dt + 0.0001) {
        const need = 12 - g.ammo;
        const take = Math.min(need, g.reserve);
        g.ammo += take;
        g.reserve -= take;
        setAmmo(g.ammo);
        setReserve(g.reserve);
      }
    }

    // Shooting request.
    const wantShoot = inputRef.current.shoot || keys.Space;
    if (wantShoot) {
      inputRef.current.shoot = false;
      keys.Space = false;
      if (g.reloading <= 0 && g.fireCooldown <= 0 && g.ammo > 0) {
        g.fireCooldown = 0.13;
        g.ammo -= 1;
        setAmmo(g.ammo);
        g.muzzle = 0.08;
        g.recoil = 0.12;
        g.shake = 0.25;
        // Hitscan: check enemy closest to crosshair and not behind wall.
        const wall = castWall(g.x, g.y, g.dir);
        let best = null;
        for (const e of g.enemies) {
          const ex = e.x - g.x;
          const ey = e.y - g.y;
          const dist = Math.hypot(ex, ey);
          if (dist < 0.0001) continue;
          const ang = Math.atan2(ey, ex);
          const da = wrapAngle(ang - g.dir);
          if (Math.abs(da) > 0.055) continue;
          if (dist > wall.dist + 0.2) continue;
          if (!best || dist < best.dist) best = { e, dist };
        }
        if (best) {
          best.e.hp -= 1;
          g.hitMarker = 0.12;
          if (best.e.hp <= 0) {
            g.score += 40;
            g.kills += 1;
            setKills(g.kills);
            setScore(Math.floor(g.score));
            g.enemies = g.enemies.filter((x) => x !== best.e);
          } else {
            g.score += 10;
            setScore(Math.floor(g.score));
          }
        }
      } else if (g.ammo <= 0 && g.reserve > 0) {
        // Auto-reload on empty.
        if (g.reloading <= 0) g.reloading = 0.9;
      }
    }

    // Spawn enemies.
    g.t += dt;
    if (g.t >= g.spawnAt && g.enemies.length < cfg.maxEnemies) {
      g.spawnAt = g.t + cfg.spawnEvery * (0.8 + Math.random() * 0.4);
      spawnEnemy();
    }

    // Enemy movement + bite.
    for (const e of g.enemies) {
      e.t += dt;
      e.bite = Math.max(0, (e.bite || 0) - dt);
      const dxp = g.x - e.x;
      const dyp = g.y - e.y;
      const dist = Math.hypot(dxp, dyp);
      if (dist > 0.001) {
        const vx = (dxp / dist) * e.spd;
        const vy = (dyp / dist) * e.spd;
        const ex = e.x + vx * dt;
        const ey = e.y + vy * dt;
        if (!isWall(ex, e.y)) e.x = ex;
        if (!isWall(e.x, ey)) e.y = ey;
      }
      if (dist < 0.62 && e.bite <= 0) {
        e.bite = 0.65;
        g.hp = Math.max(0, g.hp - (6 + Math.floor(cfg.level * 1.5)));
        g.shake = Math.max(g.shake, 0.35);
        setHp(g.hp);
        if (g.hp <= 0) {
          setGameOver(true);
          stopGame();
        }
      }
    }

    // Level progression.
    if (g.kills >= g.killsNeeded) {
      const next = Math.min(levels.length, g.level + 1);
      g.score += 100;
      setScore(Math.floor(g.score));
      startLevel(next);
    }

    setLevel(g.level);
    setKillsNeeded(g.killsNeeded);

    g.score += dt * 6;
    setScore(Math.floor(g.score));

    const sky = ctx.createLinearGradient(0, 0, 0, h * 0.6);
    sky.addColorStop(0, '#0b1238');
    sky.addColorStop(1, '#0b0f25');
    ctx.fillStyle = sky;
    ctx.fillRect(0, 0, w, h);

    const floor = ctx.createLinearGradient(0, h * 0.5, 0, h);
    floor.addColorStop(0, '#0a0a12');
    floor.addColorStop(1, '#050507');
    const bobY = Math.sin(g.bobT) * 10 * g.bob;
    const pitchY = clamp(g.pitch, -0.65, 0.65) * (h * 0.22);
    const horizon = h * 0.52 + bobY + pitchY;
    ctx.fillStyle = floor;
    ctx.fillRect(0, horizon, w, h - horizon);

    // Camera shake.
    const shakeX = (Math.random() - 0.5) * 10 * g.shake;
    const shakeY = (Math.random() - 0.5) * 10 * g.shake;
    ctx.save();
    ctx.translate(shakeX, shakeY);

    const fov = cfg.fov;
    const zBuffer = new Array(Math.floor(w));
    for (let x = 0; x < w; x += 1) {
      const cameraX = (2 * x) / w - 1;
      const ang = g.dir + Math.atan(cameraX * Math.tan(fov / 2));
      const wall = castWall(g.x, g.y, ang);
      const perpWallDist = wall.dist;
      zBuffer[Math.floor(x)] = perpWallDist;

      const lineHeight = Math.min(h, (h * 0.92) / perpWallDist);
      const drawStart = Math.max(0, horizon - lineHeight * 0.5);
      const shade = wall.side === 1 ? 0.68 : 1;
      const fog = Math.max(0, 1 - perpWallDist / 10);
      const r = Math.floor(120 * shade * fog + 30);
      const gcol = Math.floor(80 * shade * fog + 20);
      const b = Math.floor(220 * shade * fog + 40);
      ctx.strokeStyle = `rgb(${r},${gcol},${b})`;
      ctx.beginPath();
      ctx.moveTo(x + 0.5, drawStart);
      ctx.lineTo(x + 0.5, drawStart + lineHeight);
      ctx.stroke();
    }

    // Draw enemy billboards.
    const enemies = gameRef.current.enemies;
    const sorted = [...enemies].sort((a, b) => {
      const da = (a.x - g.x) ** 2 + (a.y - g.y) ** 2;
      const db = (b.x - g.x) ** 2 + (b.y - g.y) ** 2;
      return db - da;
    });
    for (const e of sorted) {
      const ex = e.x - g.x;
      const ey = e.y - g.y;
      const dist = Math.hypot(ex, ey);
      if (dist < 0.001) continue;
      const angTo = Math.atan2(ey, ex);
      const da = wrapAngle(angTo - g.dir);
      if (Math.abs(da) > fov * 0.52) continue;
      const sxp = (0.5 + (da / fov)) * w;
      const size = clamp((h * 0.7) / dist, 10, h);
      const x0 = sxp - size * 0.35;
      const y0 = horizon - size * 0.5;
      const col = Math.floor(clamp(sxp, 0, w - 1));
      const wallD = zBuffer[col] || 999;
      if (dist > wallD + 0.1) continue;

      ctx.save();
      const glow = ctx.createLinearGradient(x0, y0, x0, y0 + size);
      glow.addColorStop(0, 'rgba(255,60,190,0.10)');
      glow.addColorStop(0.5, 'rgba(255,60,190,0.65)');
      glow.addColorStop(1, 'rgba(255,60,190,0.10)');
      ctx.fillStyle = glow;
      ctx.shadowColor = 'rgba(255,60,190,0.65)';
      ctx.shadowBlur = 22;
      ctx.fillRect(x0, y0, size * 0.7, size);
      ctx.shadowBlur = 0;

      // Core.
      ctx.fillStyle = 'rgba(255,190,240,0.85)';
      ctx.fillRect(x0 + size * 0.26, y0 + size * 0.18, size * 0.18, size * 0.64);

      // Tiny HP pips.
      const hpP = Math.max(0, Math.min(3, e.hp));
      for (let i = 0; i < hpP; i += 1) {
        ctx.fillStyle = 'rgba(255,255,255,0.75)';
        ctx.fillRect(x0 + i * 6, y0 - 8, 4, 4);
      }
      ctx.restore();
    }

    ctx.save();
    ctx.strokeStyle = 'rgba(255,255,255,0.6)';
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.moveTo(w / 2 - 8, horizon);
    ctx.lineTo(w / 2 + 8, horizon);
    ctx.moveTo(w / 2, horizon - 8);
    ctx.lineTo(w / 2, horizon + 8);
    ctx.stroke();
    if (g.hitMarker > 0) {
      ctx.strokeStyle = 'rgba(255,255,255,0.85)';
      ctx.lineWidth = 3;
      ctx.beginPath();
      ctx.moveTo(w / 2 - 14, horizon - 14);
      ctx.lineTo(w / 2 - 6, horizon - 6);
      ctx.moveTo(w / 2 + 14, horizon - 14);
      ctx.lineTo(w / 2 + 6, horizon - 6);
      ctx.moveTo(w / 2 - 14, horizon + 14);
      ctx.lineTo(w / 2 - 6, horizon + 6);
      ctx.moveTo(w / 2 + 14, horizon + 14);
      ctx.lineTo(w / 2 + 6, horizon + 6);
      ctx.stroke();
    }
    ctx.restore();

    // Weapon + body overlay.
    const swayX = Math.sin(g.bobT * 0.9) * 10 * g.bob;
    const swayY = Math.cos(g.bobT * 1.1) * 8 * g.bob;
    const recoilY = -g.recoil * 38;
    const gunX = w * 0.5 + swayX;
    const gunY = h - 70 + swayY + recoilY;

    // Feet (simple step arcs).
    const step = Math.sin(g.bobT * 1.4) * 10 * g.bob;
    ctx.save();
    ctx.globalAlpha = 0.65;
    ctx.fillStyle = 'rgba(140,180,255,0.08)';
    ctx.strokeStyle = 'rgba(140,180,255,0.22)';
    ctx.lineWidth = 2;
    const footY = h - 18;
    ctx.beginPath();
    ctx.ellipse(w * 0.43, footY + step, 34, 14, 0.1, 0, Math.PI * 2);
    ctx.ellipse(w * 0.57, footY - step, 34, 14, -0.1, 0, Math.PI * 2);
    ctx.fill();
    ctx.stroke();
    ctx.restore();

    // Hands.
    ctx.save();
    ctx.translate(gunX, gunY);
    ctx.shadowColor = 'rgba(120,220,255,0.25)';
    ctx.shadowBlur = 18;
    ctx.fillStyle = 'rgba(240,240,255,0.22)';
    ctx.fillRect(-120, 18, 70, 44);
    ctx.fillRect(50, 30, 70, 44);
    ctx.shadowBlur = 0;

    const roundRect = (x, y, ww, hh, rr) => {
      const r = Math.max(0, Math.min(rr, ww / 2, hh / 2));
      if (typeof ctx.roundRect === 'function') {
        ctx.beginPath();
        ctx.roundRect(x, y, ww, hh, r);
        return;
      }
      ctx.beginPath();
      ctx.moveTo(x + r, y);
      ctx.lineTo(x + ww - r, y);
      ctx.quadraticCurveTo(x + ww, y, x + ww, y + r);
      ctx.lineTo(x + ww, y + hh - r);
      ctx.quadraticCurveTo(x + ww, y + hh, x + ww - r, y + hh);
      ctx.lineTo(x + r, y + hh);
      ctx.quadraticCurveTo(x, y + hh, x, y + hh - r);
      ctx.lineTo(x, y + r);
      ctx.quadraticCurveTo(x, y, x + r, y);
    };

    // Gun body.
    const body = ctx.createLinearGradient(-40, 0, 80, 0);
    body.addColorStop(0, 'rgba(20,24,40,0.9)');
    body.addColorStop(1, 'rgba(70,90,180,0.85)');
    ctx.fillStyle = body;
    ctx.strokeStyle = 'rgba(160,200,255,0.35)';
    ctx.lineWidth = 2;
    roundRect(-30, -14, 160, 52, 10);
    ctx.closePath();
    ctx.fill();
    ctx.stroke();

    // Barrel.
    ctx.fillStyle = 'rgba(170,200,255,0.22)';
    ctx.fillRect(110, -6, 60, 10);
    ctx.fillRect(110, 14, 60, 10);

    // Sight.
    ctx.fillStyle = 'rgba(255,255,255,0.18)';
    ctx.fillRect(25, -26, 20, 12);

    // Muzzle flash.
    if (g.muzzle > 0) {
      ctx.save();
      ctx.globalAlpha = clamp(g.muzzle / 0.08, 0, 1);
      ctx.shadowColor = 'rgba(255,255,255,0.9)';
      ctx.shadowBlur = 28;
      ctx.fillStyle = 'rgba(255,245,200,0.85)';
      ctx.beginPath();
      ctx.arc(175, 10, 18, 0, Math.PI * 2);
      ctx.fill();
      ctx.restore();
    }

    ctx.restore();

    // Level banner.
    if (g.levelBanner > 0) {
      const a = clamp(g.levelBanner / 1.15, 0, 1);
      ctx.save();
      ctx.globalAlpha = 0.65 * a;
      ctx.fillStyle = 'rgba(5,8,18,0.65)';
      ctx.fillRect(w * 0.5 - 160, 22, 320, 40);
      ctx.strokeStyle = 'rgba(120,180,255,0.25)';
      ctx.strokeRect(w * 0.5 - 160, 22, 320, 40);
      ctx.fillStyle = 'rgba(255,255,255,0.9)';
      ctx.font = '600 16px system-ui, -apple-system, Segoe UI, Roboto, sans-serif';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText(`LEVEL ${g.level}`, w * 0.5, 42);
      ctx.restore();
    }

    ctx.restore();

    rafRef.current = requestAnimationFrame(tick);
  };

  useEffect(() => {
    const resize = () => {
      const canvas = canvasRef.current;
      const wrap = wrapRef.current;
      if (!canvas || !wrap) return;
      const rect = wrap.getBoundingClientRect();
      const dpr = Math.min(2, window.devicePixelRatio || 1);
      canvas.width = rect.width * dpr;
      canvas.height = rect.height * dpr;
      canvas.style.width = `${rect.width}px`;
      canvas.style.height = `${rect.height}px`;
      const ctx = canvas.getContext('2d');
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };
    resize();
    window.addEventListener('resize', resize);
    return () => window.removeEventListener('resize', resize);
  }, []);

  // Touch/mobile: allow dragging on the canvas to look around (no pointer-lock needed).
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return () => {};

    let dragging = false;
    let lastX = 0;
    let lastY = 0;

    const onDown = (e) => {
      if (!runningRef.current) return;
      if (pointerLockedRef.current) return;
      dragging = true;
      lastX = e.clientX;
      lastY = e.clientY;
      try {
        if (canvas.setPointerCapture) canvas.setPointerCapture(e.pointerId);
      } catch (err) {
        // ignore
      }
    };

    const onMove = (e) => {
      if (!runningRef.current) return;
      if (!dragging) return;
      if (pointerLockedRef.current) return;
      const g = gameRef.current;
      const dx = (e.clientX - lastX) || 0;
      const dy = (e.clientY - lastY) || 0;
      lastX = e.clientX;
      lastY = e.clientY;
      g.dir += dx * 0.0032;
      g.pitch = clamp(g.pitch + dy * 0.0024, -0.65, 0.65);
    };

    const onUp = () => {
      dragging = false;
    };

    canvas.addEventListener('pointerdown', onDown);
    canvas.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
    window.addEventListener('pointercancel', onUp);
    return () => {
      canvas.removeEventListener('pointerdown', onDown);
      canvas.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
      window.removeEventListener('pointercancel', onUp);
    };
  }, []);

  useEffect(() => {
    return () => {
      if (shootHoldRef.current) clearInterval(shootHoldRef.current);
      shootHoldRef.current = null;
    };
  }, []);

  useEffect(() => {
    const onKeyDown = (e) => {
      keysRef.current[e.code] = true;
      if (e.code === 'Space') e.preventDefault();
    };
    const onKeyUp = (e) => {
      keysRef.current[e.code] = false;
    };
    window.addEventListener('keydown', onKeyDown);
    window.addEventListener('keyup', onKeyUp);
    return () => {
      window.removeEventListener('keydown', onKeyDown);
      window.removeEventListener('keyup', onKeyUp);
    };
  }, []);

  useEffect(() => {
    const onPointerLockChange = () => {
      const el = document.pointerLockElement;
      pointerLockedRef.current = Boolean(el && wrapRef.current && wrapRef.current.contains(el));
    };
    document.addEventListener('pointerlockchange', onPointerLockChange);
    return () => document.removeEventListener('pointerlockchange', onPointerLockChange);
  }, []);

  useEffect(() => {
    const onMouseMove = (e) => {
      if (!runningRef.current) return;
      if (!pointerLockedRef.current) return;
      const g = gameRef.current;
      const dx = e.movementX || 0;
      const dy = e.movementY || 0;
      g.dir += dx * 0.0032;
      g.pitch = clamp(g.pitch + dy * 0.0024, -0.65, 0.65);
    };
    window.addEventListener('mousemove', onMouseMove);
    return () => window.removeEventListener('mousemove', onMouseMove);
  }, []);

  useEffect(() => {
    return () => {
      runningRef.current = false;
      setIsRunning(false);
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, []);

  return (
    <GameShell
      title="Neon FPS"
      subtitle="WASD move • Q/E strafe • Mouse to look (click to lock) • Space/click shoot • R reload • Survive and clear levels."
    >
      <GameCanvas
        wrapRef={wrapRef}
        canvasRef={canvasRef}
        score={score}
        lives={hp}
        bestScore={null}
        gameOver={gameOver}
        isRunning={isRunning}
        onStart={startGame}
        onPause={stopGame}
        onClick={() => {
          if (!runningRef.current) return;
          inputRef.current.shoot = true;
          const canvas = canvasRef.current;
          if (canvas && canvas.requestPointerLock) {
            try { canvas.requestPointerLock(); } catch (e) {}
          }
        }}
        hudExtra={(
          <>
            <strong>Level:</strong> {level}
            <span style={{ opacity: 0.7 }}>•</span>
            <strong>Kills:</strong> {kills}/{killsNeeded}
            <span style={{ opacity: 0.7 }}>•</span>
            <strong>Ammo:</strong> {ammo}/{reserve}
          </>
        )}
        controls={(
          <div>
            <div><strong>Move:</strong> WASD</div>
            <div><strong>Strafe:</strong> Q / E</div>
            <div><strong>Look:</strong> mouse (click to lock) or touch-drag</div>
            <div><strong>Shoot:</strong> click or Space • <strong>Reload:</strong> R</div>
            <div><strong>Mobile:</strong> use on-screen buttons + drag to look</div>
            <div><strong>Exit pointer-lock:</strong> Esc</div>
            <div><strong>Fullscreen:</strong> use the Fullscreen button (Esc exits)</div>
          </div>
        )}
        overlay={(
          <div style={{ position: 'absolute', inset: 0, pointerEvents: 'none' }}>
            {/* Touch controls (pointerEvents enabled only on the buttons) */}
            <div
              style={{
                position: 'absolute',
                left: 14,
                bottom: 70,
                display: 'grid',
                gridTemplateColumns: 'repeat(3, 46px)',
                gridTemplateRows: 'repeat(3, 46px)',
                gap: 8,
                pointerEvents: 'auto',
                opacity: 0.95,
              }}
            >
              <div />
              <button
                className="btn-glass-secondary"
                type="button"
                onPointerDown={() => { keysRef.current.KeyW = true; }}
                onPointerUp={() => { keysRef.current.KeyW = false; }}
                onPointerCancel={() => { keysRef.current.KeyW = false; }}
              >
                W
              </button>
              <div />

              <button
                className="btn-glass-secondary"
                type="button"
                onPointerDown={() => { keysRef.current.KeyQ = true; }}
                onPointerUp={() => { keysRef.current.KeyQ = false; }}
                onPointerCancel={() => { keysRef.current.KeyQ = false; }}
              >
                Q
              </button>
              <button
                className="btn-glass-secondary"
                type="button"
                onPointerDown={() => { keysRef.current.KeyS = true; }}
                onPointerUp={() => { keysRef.current.KeyS = false; }}
                onPointerCancel={() => { keysRef.current.KeyS = false; }}
              >
                S
              </button>
              <button
                className="btn-glass-secondary"
                type="button"
                onPointerDown={() => { keysRef.current.KeyE = true; }}
                onPointerUp={() => { keysRef.current.KeyE = false; }}
                onPointerCancel={() => { keysRef.current.KeyE = false; }}
              >
                E
              </button>

              <div />
              <button
                className="btn-glass-secondary"
                type="button"
                onPointerDown={() => { keysRef.current.KeyA = true; }}
                onPointerUp={() => { keysRef.current.KeyA = false; }}
                onPointerCancel={() => { keysRef.current.KeyA = false; }}
              >
                A
              </button>
              <button
                className="btn-glass-secondary"
                type="button"
                onPointerDown={() => { keysRef.current.KeyD = true; }}
                onPointerUp={() => { keysRef.current.KeyD = false; }}
                onPointerCancel={() => { keysRef.current.KeyD = false; }}
              >
                D
              </button>
            </div>

            <div style={{ position: 'absolute', right: 14, bottom: 70, display: 'grid', gap: 10, pointerEvents: 'auto' }}>
              <button
                className="btn-glass-primary"
                type="button"
                onPointerDown={() => {
                  if (!runningRef.current) return;
                  inputRef.current.shoot = true;
                  if (shootHoldRef.current) clearInterval(shootHoldRef.current);
                  shootHoldRef.current = setInterval(() => {
                    if (!runningRef.current) return;
                    inputRef.current.shoot = true;
                  }, 140);
                }}
                onPointerUp={() => {
                  if (shootHoldRef.current) clearInterval(shootHoldRef.current);
                  shootHoldRef.current = null;
                }}
                onPointerCancel={() => {
                  if (shootHoldRef.current) clearInterval(shootHoldRef.current);
                  shootHoldRef.current = null;
                }}
              >
                Shoot
              </button>
              <button
                className="btn-glass-secondary"
                type="button"
                onClick={() => {
                  if (!runningRef.current) return;
                  inputRef.current.reload = true;
                }}
              >
                Reload
              </button>
            </div>
          </div>
        )}
      />
    </GameShell>
  );
}

function GameCanvas({
  wrapRef,
  canvasRef,
  score,
  lives,
  bestScore,
  hudExtra,
  controls,
  overlay,
  gameOver,
  isRunning,
  onStart,
  onPause,
  onClick,
}) {
  const [showControls, setShowControls] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [isPseudoFullscreen, setIsPseudoFullscreen] = useState(false);

  useEffect(() => {
    const wrap = wrapRef && 'current' in wrapRef ? wrapRef.current : null;
    if (!wrap) return () => {};

    const update = () => {
      const fsEl = document.fullscreenElement || document.webkitFullscreenElement;
      setIsFullscreen(Boolean(fsEl && fsEl === wrap));
      try { window.dispatchEvent(new Event('resize')); } catch (e) {}
    };

    document.addEventListener('fullscreenchange', update);
    document.addEventListener('webkitfullscreenchange', update);
    update();
    return () => {
      document.removeEventListener('fullscreenchange', update);
      document.removeEventListener('webkitfullscreenchange', update);
    };
  }, [wrapRef]);

  useEffect(() => {
    const onKeyDown = (e) => {
      if (e.key === 'Escape') {
        setIsPseudoFullscreen(false);
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, []);

  const toggleFullscreen = async () => {
    const wrap = wrapRef && 'current' in wrapRef ? wrapRef.current : null;
    if (!wrap) return;

    const fsEl = document.fullscreenElement || document.webkitFullscreenElement;
    const isFs = Boolean(fsEl && fsEl === wrap);

    try {
      if (!isFs) {
        if (wrap.requestFullscreen || wrap.webkitRequestFullscreen) {
          if (wrap.requestFullscreen) await wrap.requestFullscreen();
          else wrap.webkitRequestFullscreen();
        } else {
          setIsPseudoFullscreen(true);
        }
      } else if (document.exitFullscreen) {
        await document.exitFullscreen();
      } else if (document.webkitExitFullscreen) {
        document.webkitExitFullscreen();
      }
    } catch (e) {
      // Fallback for browsers that block Fullscreen API (notably some mobile/iframe cases)
      setIsPseudoFullscreen((v) => !v);
    }
  };

  return (
    <div
      ref={wrapRef}
      onClick={onClick}
      role="presentation"
      style={{
        position: 'relative',
        width: '100%',
        height: (isFullscreen || isPseudoFullscreen) ? '100vh' : '62vh',
        minHeight: (isFullscreen || isPseudoFullscreen) ? '100vh' : 420,
        borderRadius: (isFullscreen || isPseudoFullscreen) ? 0 : 16,
        overflow: 'hidden',
        background: 'rgba(10,12,24,0.8)',
        border: '1px solid rgba(120,140,255,0.25)',
        boxShadow: '0 20px 80px rgba(0,0,0,0.55), inset 0 0 40px rgba(120,140,255,0.15)',
        ...(isPseudoFullscreen ? {
          position: 'fixed',
          inset: 0,
          width: '100vw',
          height: '100vh',
          zIndex: 9999,
        } : {}),
      }}
    >
      <canvas ref={canvasRef} style={{ display: 'block' }} />

      {overlay ? overlay : null}

      <div
        style={{
          position: 'absolute',
          top: 12,
          left: 12,
          display: 'flex',
          gap: 10,
          alignItems: 'center',
          padding: '8px 12px',
          background: 'rgba(7,10,18,0.65)',
          borderRadius: 12,
          border: '1px solid rgba(120,140,255,0.25)',
          backdropFilter: 'blur(6px)',
          flexWrap: 'wrap',
        }}
      >
        <strong>Score:</strong> {score}
        {typeof lives === 'number' ? (
          <>
            <span style={{ opacity: 0.7 }}>•</span>
            <strong>Lives:</strong> {lives}
          </>
        ) : null}
        {typeof bestScore === 'number' ? (
          <>
            <span style={{ opacity: 0.7 }}>•</span>
            <strong>Best:</strong> {bestScore}
          </>
        ) : null}
        {hudExtra ? hudExtra : null}
      </div>

      <div
        style={{
          position: 'absolute',
          bottom: 16,
          left: 16,
          right: 16,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: 12,
        }}
      >
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
          {controls ? (
            <button
              className="btn-glass-secondary"
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                setShowControls(true);
              }}
            >
              Controls
            </button>
          ) : null}
          <button
            className="btn-glass-secondary"
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              toggleFullscreen();
            }}
          >
            {(isFullscreen || isPseudoFullscreen) ? 'Exit Fullscreen' : 'Fullscreen'}
          </button>
        </div>

        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
        {!isRunning && !gameOver ? (
          <button className="btn-glass-primary" type="button" onClick={onStart}>Start</button>
        ) : null}
        {gameOver ? (
          <button className="btn-glass-primary" type="button" onClick={onStart}>Restart</button>
        ) : isRunning ? (
          <button className="btn-glass-secondary" type="button" onClick={onPause}>Pause</button>
        ) : null}
        </div>
      </div>

      {showControls ? (
        <div
          role="presentation"
          onClick={() => setShowControls(false)}
          style={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: 'rgba(5,8,18,0.72)',
            backdropFilter: 'blur(10px)',
            padding: 16,
            textAlign: 'left',
          }}
        >
          <div
            role="presentation"
            onClick={(e) => e.stopPropagation()}
            style={{
              maxWidth: 520,
              width: '100%',
              borderRadius: 16,
              border: '1px solid rgba(120,140,255,0.28)',
              background: 'rgba(10,12,24,0.86)',
              boxShadow: '0 30px 120px rgba(0,0,0,0.65)',
              padding: 14,
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
              <div style={{ fontWeight: 800, fontSize: 16 }}>Controls</div>
              <button className="btn-glass-secondary" type="button" onClick={() => setShowControls(false)}>
                Close
              </button>
            </div>
            <div className="meta-text" style={{ marginTop: 10, lineHeight: 1.6 }}>
              {controls}
            </div>
          </div>
        </div>
      ) : null}

      {gameOver ? (
        <div
          style={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: 'rgba(5,8,18,0.7)',
            textAlign: 'center',
            backdropFilter: 'blur(8px)',
          }}
        >
          <div>
            <div style={{ fontSize: 28, fontWeight: 700, marginBottom: 8 }}>Run Complete</div>
            <div className="meta-text">Score: {score}</div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
/*
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { useAppConfig } from '../context/AppConfigContext';
import { isAdminEmail } from '../utils/admin';

export default function SecretPen() {
  const { user, loading } = useAuth();
  const { adminEmails } = useAppConfig();
  const isAdmin = useMemo(
    () => isAdminEmail(user?.email, adminEmails),
    [user?.email, adminEmails]
  );
  const [activeGame, setActiveGame] = useState('drift');

  if (loading) {
    return <div style={{ paddingTop: '4rem', textAlign: 'center' }}>Loading…</div>;
  }

  if (!isAdmin) {
    return (
      <div className="page-section">
        <div className="page-container" style={{ maxWidth: 1100 }}>
          <div className="glass-card" style={{ padding: 18, textAlign: 'center' }}>
            Not Found
          </div>
        </div>
      </div>
    );
  }

  const gameTabs = [
    { id: 'drift', label: 'Neon Drift' },
    { id: 'hopper', label: 'Orbit Hopper' },
    { id: 'fps', label: 'Neon FPS' },
  ];

  return (
    <div className="page-section">
      <div className="page-container" style={{ maxWidth: 1100 }}>
        <h1 className="title" style={{ textAlign: 'center', marginTop: 0 }}>Secret Arcade</h1>
        <p className="subtitle" style={{ textAlign: 'center' }}>
          Three different games. One is a full FPS shooter.
        </p>

        <div style={{ display: 'flex', justifyContent: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 12 }}>
          {gameTabs.map((tab) => (
            <button
              key={tab.id}
              type="button"
              className={activeGame === tab.id ? 'btn-glass-primary' : 'btn-glass-secondary'}
              onClick={() => setActiveGame(tab.id)}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {activeGame === 'drift' ? <NeonDriftGame /> : null}
        {activeGame === 'hopper' ? <OrbitHopperGame /> : null}
        {activeGame === 'fps' ? <NeonFPSGame /> : null}
      </div>
    </div>
  );
}

function GameShell({ title, subtitle, children }) {
  return (
    <div className="glass-card" style={{ padding: 16 }}>
      <h2 style={{ marginTop: 0, textAlign: 'center' }}>{title}</h2>
      <div className="meta-text" style={{ textAlign: 'center', marginBottom: 12 }}>{subtitle}</div>
      {children}
    </div>
  );
}

function NeonDriftGame() {
  const canvasRef = useRef(null);
  const wrapRef = useRef(null);
  const rafRef = useRef(null);
  const lastTimeRef = useRef(0);
  const pointerRef = useRef({ x: 0.5, y: 0.6, active: false });
  const runningRef = useRef(false);
  const gameRef = useRef({
    ship: { x: 0, y: 0, vx: 0, vy: 0 },
    stars: [],
    enemies: [],
    orbs: [],
    particles: [],
    score: 0,
    lives: 3,
    t: 0,
    spawnEnemyAt: 0,
    spawnOrbAt: 0,
  });

  const [score, setScore] = useState(0);
  const [lives, setLives] = useState(3);
  const [gameOver, setGameOver] = useState(false);
  const [isRunning, setIsRunning] = useState(false);
  const [bestScore, setBestScore] = useState(() => {
    try {
      const v = localStorage.getItem('SECRET_BEST_DRIFT');
      return v ? Number(v) || 0 : 0;
    } catch (e) {
      return 0;
    }
  });

  const resetGame = () => {
    const g = gameRef.current;
    g.ship = { x: 0, y: 0, vx: 0, vy: 0 };
    g.stars = [];
    g.enemies = [];
    g.orbs = [];
    g.particles = [];
    g.score = 0;
    g.lives = 3;
    g.t = 0;
    g.spawnEnemyAt = 0;
    g.spawnOrbAt = 0;
    setScore(0);
    setLives(3);
    setGameOver(false);
  };

  const startGame = () => {
    resetGame();
    runningRef.current = true;
    setIsRunning(true);
    lastTimeRef.current = performance.now();
    rafRef.current = requestAnimationFrame(tick);
  };

  const stopGame = () => {
    runningRef.current = false;
    setIsRunning(false);
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
  };

  const spawnStarfield = (count, w, h) => {
    const stars = [];
    for (let i = 0; i < count; i += 1) {
      stars.push({
        x: Math.random() * w,
        y: Math.random() * h,
        r: Math.random() * 1.6 + 0.4,
        s: Math.random() * 40 + 20,
        a: Math.random() * 0.6 + 0.2,
      });
    }
    return stars;
  };

  const emitParticles = (x, y, color, count = 18) => {
    const g = gameRef.current;
    for (let i = 0; i < count; i += 1) {
      const ang = Math.random() * Math.PI * 2;
      const spd = Math.random() * 220 + 60;
      g.particles.push({
        x,
        y,
        vx: Math.cos(ang) * spd,
        vy: Math.sin(ang) * spd,
        r: Math.random() * 3 + 1.5,
        life: Math.random() * 0.6 + 0.4,
        color,
      });
    }
  };

  const tick = (now) => {
    if (!runningRef.current) return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    const w = canvas.width;
    const h = canvas.height;
    const dt = Math.min(0.05, (now - lastTimeRef.current) / 1000);
    lastTimeRef.current = now;
    const g = gameRef.current;
    g.t += dt;

    if (!g.stars.length) {
      g.stars = spawnStarfield(Math.floor((w * h) / 6000), w, h);
    }

    const bg = ctx.createLinearGradient(0, 0, 0, h);
    bg.addColorStop(0, '#05060f');
    bg.addColorStop(0.5, '#0b0f2b');
    bg.addColorStop(1, '#04050a');
    ctx.fillStyle = bg;
    ctx.fillRect(0, 0, w, h);

    for (const s of g.stars) {
      s.y += s.s * dt;
      if (s.y > h + 20) {
        s.y = -20;
        s.x = Math.random() * w;
      }
      ctx.beginPath();
      ctx.fillStyle = `rgba(200,220,255,${s.a})`;
      ctx.arc(s.x, s.y, s.r, 0, Math.PI * 2);
      ctx.fill();
    }

    const targetX = pointerRef.current.active ? pointerRef.current.x * w : w * 0.5;
    const targetY = pointerRef.current.active ? pointerRef.current.y * h : h * 0.65;
    const ship = g.ship;
    ship.vx += (targetX - ship.x) * 4.5 * dt;
    ship.vy += (targetY - ship.y) * 4.5 * dt;
    ship.vx *= 0.9;
    ship.vy *= 0.9;
    ship.x += ship.vx;
    ship.y += ship.vy;
    if (!ship.x && !ship.y) {
      ship.x = targetX;
      ship.y = targetY;
    }

    const difficulty = 1 + Math.min(2, g.score / 20);

    if (g.t > g.spawnEnemyAt) {
      g.spawnEnemyAt = g.t + (0.65 - Math.min(0.35, g.score * 0.01));
      g.enemies.push({
        x: Math.random() * (w - 80) + 40,
        y: -30,
        r: Math.random() * 18 + 16,
        vy: Math.random() * 140 + 120 * difficulty,
        vx: (Math.random() - 0.5) * 60,
        spin: (Math.random() - 0.5) * 3,
        a: Math.random() * Math.PI * 2,
      });
    }

    if (g.t > g.spawnOrbAt) {
      g.spawnOrbAt = g.t + 1.1 + Math.random() * 0.6;
      g.orbs.push({
        x: Math.random() * (w - 120) + 60,
        y: -20,
        r: 10,
        vy: Math.random() * 120 + 90,
      });
    }

    for (let i = g.enemies.length - 1; i >= 0; i -= 1) {
      const e = g.enemies[i];
      e.y += e.vy * dt;
      e.x += e.vx * dt;
      e.a += e.spin * dt;
      if (e.y > h + 60) {
        g.enemies.splice(i, 1);
        continue;
      }
      const dx = ship.x - e.x;
      const dy = ship.y - e.y;
      const dist = Math.hypot(dx, dy);
      if (dist < e.r + 18) {
        g.enemies.splice(i, 1);
        g.lives -= 1;
        setLives(g.lives);
        emitParticles(e.x, e.y, '#ff3b5f');
        if (g.lives <= 0) {
          setGameOver(true);
          setIsRunning(false);
          if (g.score > bestScore) {
            setBestScore(g.score);
            try { localStorage.setItem('SECRET_BEST_DRIFT', String(g.score)); } catch (e) {}
          }
          stopGame();
        }
      }
    }

    for (let i = g.orbs.length - 1; i >= 0; i -= 1) {
      const o = g.orbs[i];
      o.y += o.vy * dt;
      if (o.y > h + 30) {
        g.orbs.splice(i, 1);
        continue;
      }
      const dx = ship.x - o.x;
      const dy = ship.y - o.y;
      if (Math.hypot(dx, dy) < 24) {
        g.orbs.splice(i, 1);
        g.score += 1;
        setScore(g.score);
        emitParticles(o.x, o.y, '#38f2ff', 12);
      }
    }

    for (let i = g.particles.length - 1; i >= 0; i -= 1) {
      const p = g.particles[i];
      p.life -= dt;
      if (p.life <= 0) {
        g.particles.splice(i, 1);
        continue;
      }
      p.x += p.vx * dt;
      p.y += p.vy * dt;
      p.vx *= 0.97;
      p.vy *= 0.97;
    }

    for (const o of g.orbs) {
      ctx.save();
      ctx.shadowColor = '#38f2ff';
      ctx.shadowBlur = 20;
      ctx.fillStyle = '#7df6ff';
      ctx.beginPath();
      ctx.arc(o.x, o.y, o.r, 0, Math.PI * 2);
      ctx.fill();
      ctx.restore();
    }

    for (const e of g.enemies) {
      ctx.save();
      ctx.translate(e.x, e.y);
      ctx.rotate(e.a);
      ctx.shadowColor = '#ff3b5f';
      ctx.shadowBlur = 24;
      ctx.fillStyle = '#ff2d4f';
      ctx.beginPath();
      for (let i = 0; i < 6; i += 1) {
        const ang = (Math.PI * 2 * i) / 6;
        const rad = i % 2 === 0 ? e.r : e.r * 0.6;
        const px = Math.cos(ang) * rad;
        const py = Math.sin(ang) * rad;
        if (i === 0) ctx.moveTo(px, py); else ctx.lineTo(px, py);
      }
      ctx.closePath();
      ctx.fill();
      ctx.restore();
    }

    ctx.save();
    ctx.translate(ship.x, ship.y);
    ctx.shadowColor = '#7d7bff';
    ctx.shadowBlur = 28;
    const grad = ctx.createRadialGradient(0, 0, 4, 0, 0, 28);
    grad.addColorStop(0, '#c6d1ff');
    grad.addColorStop(0.5, '#6b7dff');
    grad.addColorStop(1, '#2d2b5f');
    ctx.fillStyle = grad;
    ctx.beginPath();
    ctx.moveTo(0, -22);
    ctx.lineTo(18, 18);
    ctx.lineTo(0, 12);
    ctx.lineTo(-18, 18);
    ctx.closePath();
    ctx.fill();
    ctx.restore();

    for (const p of g.particles) {
      ctx.save();
      ctx.globalAlpha = Math.max(0, Math.min(1, p.life));
      ctx.shadowColor = p.color;
      ctx.shadowBlur = 16;
      ctx.fillStyle = p.color;
      ctx.beginPath();
      ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2);
      ctx.fill();
      ctx.restore();
    }

    rafRef.current = requestAnimationFrame(tick);
  };

  useEffect(() => {
    const resize = () => {
      const canvas = canvasRef.current;
      const wrap = wrapRef.current;
      if (!canvas || !wrap) return;
      const rect = wrap.getBoundingClientRect();
      const dpr = Math.min(2, window.devicePixelRatio || 1);
      canvas.width = rect.width * dpr;
      canvas.height = rect.height * dpr;
      canvas.style.width = `${rect.width}px`;
      canvas.style.height = `${rect.height}px`;
      const ctx = canvas.getContext('2d');
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      gameRef.current.stars = [];
    };
    resize();
    window.addEventListener('resize', resize);
    return () => window.removeEventListener('resize', resize);
  }, []);

  useEffect(() => {
    const wrap = wrapRef.current;
    if (!wrap) return () => {};
    const onMove = (e) => {
      const rect = wrap.getBoundingClientRect();
      pointerRef.current = {
        x: (e.clientX - rect.left) / rect.width,
        y: (e.clientY - rect.top) / rect.height,
        active: true,
      };
    };
    const onLeave = () => {
      pointerRef.current.active = false;
    };
    wrap.addEventListener('pointermove', onMove);
    wrap.addEventListener('pointerleave', onLeave);
    return () => {
      wrap.removeEventListener('pointermove', onMove);
      wrap.removeEventListener('pointerleave', onLeave);
    };
  }, []);

  useEffect(() => {
    return () => {
      runningRef.current = false;
      setIsRunning(false);
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, []);

  return (
    <GameShell
      title="Neon Drift"
      subtitle="Collect cyan orbs. Dodge red rifts. Move with mouse or touch."
    >
      <GameCanvas
        wrapRef={wrapRef}
        canvasRef={canvasRef}
        score={score}
        lives={lives}
        bestScore={bestScore}
        gameOver={gameOver}
        isRunning={isRunning}
        onStart={startGame}
        onPause={stopGame}
      />
    </GameShell>
  );
}

function OrbitHopperGame() {
  const canvasRef = useRef(null);
  const wrapRef = useRef(null);
  const rafRef = useRef(null);
  const lastRef = useRef(0);
  const keysRef = useRef({ jump: false });
  const runningRef = useRef(false);
  const gameRef = useRef({
    player: { x: 160, y: 0, vy: 0, r: 16, onGround: false },
    platforms: [],
    stars: [],
    score: 0,
    t: 0,
  });

  const [score, setScore] = useState(0);
  const [gameOver, setGameOver] = useState(false);
  const [isRunning, setIsRunning] = useState(false);
  const [bestScore, setBestScore] = useState(() => {
    try {
      const v = localStorage.getItem('SECRET_BEST_HOPPER');
      return v ? Number(v) || 0 : 0;
    } catch (e) {
      return 0;
    }
  });

  const resetGame = (w, h) => {
    const g = gameRef.current;
    g.player = { x: 160, y: h * 0.5, vy: 0, r: 16, onGround: false };
    g.platforms = [];
    g.stars = [];
    g.score = 0;
    g.t = 0;
    const baseY = h * 0.7;
    for (let i = 0; i < 6; i += 1) {
      g.platforms.push({
        x: i * 180,
        y: baseY - (i % 2) * 60,
        w: 140,
        h: 16,
      });
      g.stars.push({
        x: i * 180 + 70,
        y: baseY - (i % 2) * 60 - 40,
        r: 8,
        active: true,
      });
    }
    setScore(0);
    setGameOver(false);
  };

  const startGame = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    resetGame(canvas.width, canvas.height);
    runningRef.current = true;
    setIsRunning(true);
    lastRef.current = performance.now();
    rafRef.current = requestAnimationFrame(tick);
  };

  const stopGame = () => {
    runningRef.current = false;
    setIsRunning(false);
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
  };

  const tick = (now) => {
    if (!runningRef.current) return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    const w = canvas.width;
    const h = canvas.height;
    const dt = Math.min(0.05, (now - lastRef.current) / 1000);
    lastRef.current = now;
    const g = gameRef.current;

    const bg = ctx.createLinearGradient(0, 0, 0, h);
    bg.addColorStop(0, '#0a0b16');
    bg.addColorStop(1, '#06070f');
    ctx.fillStyle = bg;
    ctx.fillRect(0, 0, w, h);

    const speed = 220 + g.score * 4;
    const gravity = 1200;
    const player = g.player;

    player.vy += gravity * dt;
    player.y += player.vy * dt;
    player.onGround = false;

    for (const p of g.platforms) {
      p.x -= speed * dt;
    }
    for (const s of g.stars) {
      s.x -= speed * dt;
    }

    const lastPlatform = g.platforms[g.platforms.length - 1];
    if (lastPlatform && lastPlatform.x < w - 180) {
      const newY = (Math.random() * 0.4 + 0.4) * h;
      const newX = w + Math.random() * 120;
      g.platforms.push({ x: newX, y: newY, w: 150, h: 16 });
      g.stars.push({ x: newX + 80, y: newY - 40, r: 8, active: true });
    }

    while (g.platforms.length && g.platforms[0].x + g.platforms[0].w < -60) {
      g.platforms.shift();
    }
    while (g.stars.length && g.stars[0].x < -60) {
      g.stars.shift();
    }

    for (const p of g.platforms) {
      const withinX = player.x + player.r > p.x && player.x - player.r < p.x + p.w;
      const falling = player.vy > 0;
      if (withinX && falling && player.y + player.r >= p.y && player.y + player.r <= p.y + p.h + 12) {
        player.y = p.y - player.r;
        player.vy = 0;
        player.onGround = true;
      }
      ctx.fillStyle = 'rgba(120,150,255,0.3)';
      ctx.fillRect(p.x, p.y, p.w, p.h);
      ctx.fillStyle = 'rgba(120,150,255,0.8)';
      ctx.fillRect(p.x, p.y, p.w, 3);
    }

    if ((keysRef.current.jump || player.onGround) && player.onGround) {
      player.vy = -520;
      player.onGround = false;
      keysRef.current.jump = false;
    }

    for (const s of g.stars) {
      if (!s.active) continue;
      const dx = player.x - s.x;
      const dy = player.y - s.y;
      if (Math.hypot(dx, dy) < player.r + s.r + 6) {
        s.active = false;
        g.score += 1;
        setScore(g.score);
      }
      if (s.active) {
        ctx.save();
        ctx.shadowColor = '#ffe66d';
        ctx.shadowBlur = 18;
        ctx.fillStyle = '#fff3b0';
        ctx.beginPath();
        ctx.arc(s.x, s.y, s.r, 0, Math.PI * 2);
        ctx.fill();
        ctx.restore();
      }
    }

    ctx.save();
    ctx.shadowColor = '#8ef6ff';
    ctx.shadowBlur = 18;
    ctx.fillStyle = '#5de2ff';
    ctx.beginPath();
    ctx.arc(player.x, player.y, player.r, 0, Math.PI * 2);
    ctx.fill();
    ctx.restore();

    g.t += dt;
    if (player.y > h + 80) {
      setGameOver(true);
      setIsRunning(false);
      if (g.score > bestScore) {
        setBestScore(g.score);
        try { localStorage.setItem('SECRET_BEST_HOPPER', String(g.score)); } catch (e) {}
      }
      stopGame();
    }

    rafRef.current = requestAnimationFrame(tick);
  };

  useEffect(() => {
    const resize = () => {
      const canvas = canvasRef.current;
      const wrap = wrapRef.current;
      if (!canvas || !wrap) return;
      const rect = wrap.getBoundingClientRect();
      const dpr = Math.min(2, window.devicePixelRatio || 1);
      canvas.width = rect.width * dpr;
      canvas.height = rect.height * dpr;
      canvas.style.width = `${rect.width}px`;
      canvas.style.height = `${rect.height}px`;
      const ctx = canvas.getContext('2d');
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };
    resize();
    window.addEventListener('resize', resize);
    return () => window.removeEventListener('resize', resize);
  }, []);

  useEffect(() => {
    const onKeyDown = (e) => {
      if (e.code === 'Space') keysRef.current.jump = true;
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, []);

  useEffect(() => {
    return () => {
      runningRef.current = false;
      setIsRunning(false);
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, []);

  return (
    <GameShell
      title="Orbit Hopper"
      subtitle="Auto-runner. Tap Space or click to jump and collect stars."
    >
      <GameCanvas
        wrapRef={wrapRef}
        canvasRef={canvasRef}
        score={score}
        lives={null}
        bestScore={bestScore}
        gameOver={gameOver}
        isRunning={isRunning}
        onStart={startGame}
        onPause={stopGame}
        onClick={() => { keysRef.current.jump = true; }}
      />
    </GameShell>
  );
}

function NeonFPSGame() {
  const canvasRef = useRef(null);
  const wrapRef = useRef(null);
  const rafRef = useRef(null);
  const lastRef = useRef(0);
  const keysRef = useRef({});
  const runningRef = useRef(false);
  const gameRef = useRef({
    x: 3.5,
    y: 3.5,
    dir: 0,
    score: 0,
    t: 0,
  });

  const [score, setScore] = useState(0);
  const [isRunning, setIsRunning] = useState(false);

  const map = [
    '1111111111111111',
    '1000000000100001',
    '1011110110101101',
    '1000010000100001',
    '1011011110101111',
    '1001000010000001',
    '1011010110111101',
    '1000010000100001',
    '1011110110101101',
    '1000000000000001',
    '1011110110111101',
    '1000010000100001',
    '1011011110101111',
    '1000000000100001',
    '1000000000000001',
    '1111111111111111',
  ];
  const mapW = 16;
  const mapH = 16;

  const resetGame = () => {
    const g = gameRef.current;
    g.x = 3.5;
    g.y = 3.5;
    g.dir = 0;
    g.score = 0;
    g.t = 0;
    setScore(0);
  };

  const startGame = () => {
    resetGame();
    runningRef.current = true;
    setIsRunning(true);
    lastRef.current = performance.now();
    rafRef.current = requestAnimationFrame(tick);
  };

  const stopGame = () => {
    runningRef.current = false;
    setIsRunning(false);
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
  };

  const isWall = (x, y) => {
    const mx = Math.floor(x);
    const my = Math.floor(y);
    if (mx < 0 || my < 0 || mx >= mapW || my >= mapH) return true;
    return map[my][mx] === '1';
  };

  const tick = (now) => {
    if (!runningRef.current) return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    const w = canvas.width;
    const h = canvas.height;
    const dt = Math.min(0.05, (now - lastRef.current) / 1000);
    lastRef.current = now;
    const g = gameRef.current;

    const moveSpeed = 2.6;
    const rotSpeed = 2.2;
    const dx = Math.cos(g.dir);
    const dy = Math.sin(g.dir);

    if (keysRef.current.KeyW) {
      const nx = g.x + dx * moveSpeed * dt;
      const ny = g.y + dy * moveSpeed * dt;
      if (!isWall(nx, g.y)) g.x = nx;
      if (!isWall(g.x, ny)) g.y = ny;
    }
    if (keysRef.current.KeyS) {
      const nx = g.x - dx * moveSpeed * dt;
      const ny = g.y - dy * moveSpeed * dt;
      if (!isWall(nx, g.y)) g.x = nx;
      if (!isWall(g.x, ny)) g.y = ny;
    }
    if (keysRef.current.KeyQ) {
      const nx = g.x - dy * moveSpeed * dt;
      const ny = g.y + dx * moveSpeed * dt;
      if (!isWall(nx, g.y)) g.x = nx;
      if (!isWall(g.x, ny)) g.y = ny;
    }
    if (keysRef.current.KeyE) {
      const nx = g.x + dy * moveSpeed * dt;
      const ny = g.y - dx * moveSpeed * dt;
      if (!isWall(nx, g.y)) g.x = nx;
      if (!isWall(g.x, ny)) g.y = ny;
    }
    if (keysRef.current.KeyA || keysRef.current.ArrowLeft) g.dir -= rotSpeed * dt;
    if (keysRef.current.KeyD || keysRef.current.ArrowRight) g.dir += rotSpeed * dt;

    g.t += dt;
    g.score += dt * 10;
    setScore(Math.floor(g.score));

    const sky = ctx.createLinearGradient(0, 0, 0, h * 0.6);
    sky.addColorStop(0, '#0b1238');
    sky.addColorStop(1, '#0b0f25');
    ctx.fillStyle = sky;
    ctx.fillRect(0, 0, w, h * 0.5);

    const floor = ctx.createLinearGradient(0, h * 0.5, 0, h);
    floor.addColorStop(0, '#0a0a12');
    floor.addColorStop(1, '#050507');
    ctx.fillStyle = floor;
    ctx.fillRect(0, h * 0.5, w, h * 0.5);

    const fov = Math.PI / 3;
    for (let x = 0; x < w; x += 1) {
      const cameraX = (2 * x) / w - 1;
      const rayDirX = Math.cos(g.dir) + Math.cos(g.dir + Math.PI / 2) * cameraX * Math.tan(fov / 2);
      const rayDirY = Math.sin(g.dir) + Math.sin(g.dir + Math.PI / 2) * cameraX * Math.tan(fov / 2);

      let mapX = Math.floor(g.x);
      let mapY = Math.floor(g.y);
      const deltaDistX = Math.abs(1 / (rayDirX || 0.0001));
      const deltaDistY = Math.abs(1 / (rayDirY || 0.0001));
      let sideDistX;
      let sideDistY;
      let stepX;
      let stepY;
      let side = 0;

      if (rayDirX < 0) {
        stepX = -1;
        sideDistX = (g.x - mapX) * deltaDistX;
      } else {
        stepX = 1;
        sideDistX = (mapX + 1.0 - g.x) * deltaDistX;
      }
      if (rayDirY < 0) {
        stepY = -1;
        sideDistY = (g.y - mapY) * deltaDistY;
      } else {
        stepY = 1;
        sideDistY = (mapY + 1.0 - g.y) * deltaDistY;
      }

      let hit = false;
      let safety = 0;
      while (!hit && safety < 128) {
        safety += 1;
        if (sideDistX < sideDistY) {
          sideDistX += deltaDistX;
          mapX += stepX;
          side = 0;
        } else {
          sideDistY += deltaDistY;
          mapY += stepY;
          side = 1;
        }
        if (mapY < 0 || mapY >= mapH || mapX < 0 || mapX >= mapW) {
          hit = true;
          break;
        }
        if (map[mapY][mapX] === '1') hit = true;
      }

      const perpWallDist = side === 0 ? (sideDistX - deltaDistX) : (sideDistY - deltaDistY);
      const lineHeight = Math.min(h, h / Math.max(0.1, perpWallDist));
      const drawStart = Math.max(0, (h - lineHeight) / 2);
      const shade = side === 1 ? 0.7 : 1;
      const fog = Math.max(0, 1 - perpWallDist / 9);
      const r = Math.floor(120 * shade * fog + 30);
      const gcol = Math.floor(80 * shade * fog + 20);
      const b = Math.floor(220 * shade * fog + 40);
      ctx.strokeStyle = `rgb(${r},${gcol},${b})`;
      ctx.beginPath();
      ctx.moveTo(x + 0.5, drawStart);
      ctx.lineTo(x + 0.5, drawStart + lineHeight);
      ctx.stroke();
    }

    ctx.save();
    ctx.strokeStyle = 'rgba(255,255,255,0.6)';
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.moveTo(w / 2 - 8, h / 2);
    ctx.lineTo(w / 2 + 8, h / 2);
    ctx.moveTo(w / 2, h / 2 - 8);
    ctx.lineTo(w / 2, h / 2 + 8);
    ctx.stroke();
    ctx.restore();

    rafRef.current = requestAnimationFrame(tick);
  };

  useEffect(() => {
    const resize = () => {
      const canvas = canvasRef.current;
      const wrap = wrapRef.current;
      if (!canvas || !wrap) return;
      const rect = wrap.getBoundingClientRect();
      const dpr = Math.min(2, window.devicePixelRatio || 1);
      canvas.width = rect.width * dpr;
      canvas.height = rect.height * dpr;
      canvas.style.width = `${rect.width}px`;
      canvas.style.height = `${rect.height}px`;
      const ctx = canvas.getContext('2d');
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };
    resize();
    window.addEventListener('resize', resize);
    return () => window.removeEventListener('resize', resize);
  }, []);

  useEffect(() => {
    const onKeyDown = (e) => {
      keysRef.current[e.code] = true;
    };
    const onKeyUp = (e) => {
      keysRef.current[e.code] = false;
    };
    window.addEventListener('keydown', onKeyDown);
    window.addEventListener('keyup', onKeyUp);
    return () => {
      window.removeEventListener('keydown', onKeyDown);
      window.removeEventListener('keyup', onKeyUp);
    };
  }, []);

  useEffect(() => {
    return () => {
      runningRef.current = false;
      setIsRunning(false);
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, []);

  return (
    <GameShell
      title="Neon FPS"
      subtitle="WASD to move. Q/E strafe. Arrow keys or A/D rotate. FPS-style maze."
    >
      <GameCanvas
        wrapRef={wrapRef}
        canvasRef={canvasRef}
        score={score}
        lives={null}
        bestScore={null}
        gameOver={false}
        isRunning={isRunning}
        onStart={startGame}
        onPause={stopGame}
      />
    </GameShell>
  );
}

function GameCanvas({
  wrapRef,
  canvasRef,
  score,
  lives,
  bestScore,
  gameOver,
  isRunning,
  onStart,
  onPause,
  onClick,
}) {
  return (
    <div
      ref={wrapRef}
      onClick={onClick}
      role="presentation"
      style={{
        position: 'relative',
        width: '100%',
        height: '62vh',
        minHeight: 420,
        borderRadius: 16,
        overflow: 'hidden',
        background: 'rgba(10,12,24,0.8)',
        border: '1px solid rgba(120,140,255,0.25)',
        boxShadow: '0 20px 80px rgba(0,0,0,0.55), inset 0 0 40px rgba(120,140,255,0.15)',
      }}
    >
      <canvas ref={canvasRef} style={{ display: 'block' }} />

      <div
        style={{
          position: 'absolute',
          top: 12,
          left: 12,
          display: 'flex',
          gap: 10,
          alignItems: 'center',
          padding: '8px 12px',
          background: 'rgba(7,10,18,0.65)',
          borderRadius: 12,
          border: '1px solid rgba(120,140,255,0.25)',
          backdropFilter: 'blur(6px)',
          flexWrap: 'wrap',
        }}
      >
        <strong>Score:</strong> {score}
        {typeof lives === 'number' ? (
          <>
            <span style={{ opacity: 0.7 }}>•</span>
            <strong>Lives:</strong> {lives}
          </>
        ) : null}
        {typeof bestScore === 'number' ? (
          <>
            <span style={{ opacity: 0.7 }}>•</span>
            <strong>Best:</strong> {bestScore}
          </>
        ) : null}
      </div>

      <div
        style={{
          position: 'absolute',
          bottom: 16,
          left: 16,
          right: 16,
          display: 'flex',
          justifyContent: 'flex-end',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: 12,
        }}
      >
        {!isRunning && !gameOver ? (
          <button className="btn-glass-primary" type="button" onClick={onStart}>Start</button>
        ) : null}
        {gameOver ? (
          <button className="btn-glass-primary" type="button" onClick={onStart}>Restart</button>
        ) : isRunning ? (
          <button className="btn-glass-secondary" type="button" onClick={onPause}>Pause</button>
        ) : null}
      </div>

      {gameOver ? (
        <div
          style={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: 'rgba(5,8,18,0.7)',
            textAlign: 'center',
            backdropFilter: 'blur(8px)',
          }}
        >
          <div>
            <div style={{ fontSize: 28, fontWeight: 700, marginBottom: 8 }}>Run Complete</div>
            <div className="meta-text">Score: {score}</div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
/*
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { useAppConfig } from '../context/AppConfigContext';
import { isAdminEmail } from '../utils/admin';

export default function SecretPen() {
  const { user, loading } = useAuth();
  const { adminEmails } = useAppConfig();
  const isAdmin = useMemo(
    () => isAdminEmail(user?.email, adminEmails),
    [user?.email, adminEmails]
  );
  const [activeGame, setActiveGame] = useState('drift');

  if (loading) {
    return <div style={{ paddingTop: '4rem', textAlign: 'center' }}>Loading…</div>;
  }

  if (!isAdmin) {
    return (
      <div className="page-section">
        <div className="page-container" style={{ maxWidth: 1100 }}>
          <div className="glass-card" style={{ padding: 18, textAlign: 'center' }}>
            Not Found
          </div>
        </div>
      </div>
    );
  }

  const gameTabs = [
    { id: 'drift', label: 'Neon Drift' },
    { id: 'hopper', label: 'Orbit Hopper' },
    { id: 'fps', label: 'Neon FPS' },
  ];

  return (
    <div className="page-section">
      <div className="page-container" style={{ maxWidth: 1100 }}>
        <h1 className="title" style={{ textAlign: 'center', marginTop: 0 }}>Secret Arcade</h1>
        <p className="subtitle" style={{ textAlign: 'center' }}>
          Three different games. One is a full FPS shooter.
        </p>

        <div style={{ display: 'flex', justifyContent: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 12 }}>
          {gameTabs.map((tab) => (
            <button
              key={tab.id}
              type="button"
              className={activeGame === tab.id ? 'btn-glass-primary' : 'btn-glass-secondary'}
              onClick={() => setActiveGame(tab.id)}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {activeGame === 'drift' ? <NeonDriftGame /> : null}
        {activeGame === 'hopper' ? <OrbitHopperGame /> : null}
        {activeGame === 'fps' ? <NeonFPSGame /> : null}
      </div>
    </div>
  );
}

function GameShell({ title, subtitle, children }) {
  return (
    <div className="glass-card" style={{ padding: 16 }}>
      <h2 style={{ marginTop: 0, textAlign: 'center' }}>{title}</h2>
      <div className="meta-text" style={{ textAlign: 'center', marginBottom: 12 }}>{subtitle}</div>
      {children}
    </div>
  );
}

function NeonDriftGame() {
  const canvasRef = useRef(null);
  const wrapRef = useRef(null);
  const rafRef = useRef(null);
  const lastTimeRef = useRef(0);
  const pointerRef = useRef({ x: 0.5, y: 0.6, active: false });
  const runningRef = useRef(false);
  const gameRef = useRef({
    ship: { x: 0, y: 0, vx: 0, vy: 0 },
    stars: [],
    enemies: [],
    orbs: [],
    particles: [],
    score: 0,
    lives: 3,
    t: 0,
    spawnEnemyAt: 0,
    spawnOrbAt: 0,
  });

  const [score, setScore] = useState(0);
  const [lives, setLives] = useState(3);
  const [gameOver, setGameOver] = useState(false);
  const [isRunning, setIsRunning] = useState(false);
  const [bestScore, setBestScore] = useState(() => {
    try {
      const v = localStorage.getItem('SECRET_BEST_DRIFT');
      return v ? Number(v) || 0 : 0;
    } catch (e) {
      return 0;
    }
  });

  const resetGame = () => {
    const g = gameRef.current;
    g.ship = { x: 0, y: 0, vx: 0, vy: 0 };
    g.stars = [];
    g.enemies = [];
    g.orbs = [];
    g.particles = [];
    g.score = 0;
    g.lives = 3;
    g.t = 0;
    g.spawnEnemyAt = 0;
    g.spawnOrbAt = 0;
    setScore(0);
    setLives(3);
    setGameOver(false);
  };

  const startGame = () => {
    resetGame();
    runningRef.current = true;
    setIsRunning(true);
    lastTimeRef.current = performance.now();
    rafRef.current = requestAnimationFrame(tick);
  };

  const stopGame = () => {
    runningRef.current = false;
    setIsRunning(false);
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
  };

  const spawnStarfield = (count, w, h) => {
    const stars = [];
    for (let i = 0; i < count; i += 1) {
      stars.push({
        x: Math.random() * w,
        y: Math.random() * h,
        r: Math.random() * 1.6 + 0.4,
        s: Math.random() * 40 + 20,
        a: Math.random() * 0.6 + 0.2,
      });
    }
    return stars;
  };

  const emitParticles = (x, y, color, count = 18) => {
    const g = gameRef.current;
    for (let i = 0; i < count; i += 1) {
      const ang = Math.random() * Math.PI * 2;
      const spd = Math.random() * 220 + 60;
      g.particles.push({
        x,
        y,
        vx: Math.cos(ang) * spd,
        vy: Math.sin(ang) * spd,
        r: Math.random() * 3 + 1.5,
        life: Math.random() * 0.6 + 0.4,
        color,
      });
    }
  };

  const tick = (now) => {
    if (!runningRef.current) return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    const w = canvas.width;
    const h = canvas.height;
    const dt = Math.min(0.05, (now - lastTimeRef.current) / 1000);
    lastTimeRef.current = now;
    const g = gameRef.current;
    g.t += dt;

    if (!g.stars.length) {
      g.stars = spawnStarfield(Math.floor((w * h) / 6000), w, h);
    }

    const bg = ctx.createLinearGradient(0, 0, 0, h);
    bg.addColorStop(0, '#05060f');
    bg.addColorStop(0.5, '#0b0f2b');
    bg.addColorStop(1, '#04050a');
    ctx.fillStyle = bg;
    ctx.fillRect(0, 0, w, h);

    for (const s of g.stars) {
      s.y += s.s * dt;
      if (s.y > h + 20) {
        s.y = -20;
        s.x = Math.random() * w;
      }
      ctx.beginPath();
      ctx.fillStyle = `rgba(200,220,255,${s.a})`;
      ctx.arc(s.x, s.y, s.r, 0, Math.PI * 2);
      ctx.fill();
    }

    const targetX = pointerRef.current.active ? pointerRef.current.x * w : w * 0.5;
    const targetY = pointerRef.current.active ? pointerRef.current.y * h : h * 0.65;
    const ship = g.ship;
    ship.vx += (targetX - ship.x) * 4.5 * dt;
    ship.vy += (targetY - ship.y) * 4.5 * dt;
    ship.vx *= 0.9;
    ship.vy *= 0.9;
    ship.x += ship.vx;
    ship.y += ship.vy;
    if (!ship.x && !ship.y) {
      ship.x = targetX;
      ship.y = targetY;
    }

    const difficulty = 1 + Math.min(2, g.score / 20);

    if (g.t > g.spawnEnemyAt) {
      g.spawnEnemyAt = g.t + (0.65 - Math.min(0.35, g.score * 0.01));
      g.enemies.push({
        x: Math.random() * (w - 80) + 40,
        y: -30,
        r: Math.random() * 18 + 16,
        vy: Math.random() * 140 + 120 * difficulty,
        vx: (Math.random() - 0.5) * 60,
        spin: (Math.random() - 0.5) * 3,
        a: Math.random() * Math.PI * 2,
      });
    }

    if (g.t > g.spawnOrbAt) {
      g.spawnOrbAt = g.t + 1.1 + Math.random() * 0.6;
      g.orbs.push({
        x: Math.random() * (w - 120) + 60,
        y: -20,
        r: 10,
        vy: Math.random() * 120 + 90,
      });
    }

    for (let i = g.enemies.length - 1; i >= 0; i -= 1) {
      const e = g.enemies[i];
      e.y += e.vy * dt;
      e.x += e.vx * dt;
      e.a += e.spin * dt;
      if (e.y > h + 60) {
        g.enemies.splice(i, 1);
        continue;
      }
      const dx = ship.x - e.x;
      const dy = ship.y - e.y;
      const dist = Math.hypot(dx, dy);
      if (dist < e.r + 18) {
        g.enemies.splice(i, 1);
        g.lives -= 1;
        setLives(g.lives);
        emitParticles(e.x, e.y, '#ff3b5f');
        if (g.lives <= 0) {
          setGameOver(true);
          setIsRunning(false);
          if (g.score > bestScore) {
            setBestScore(g.score);
            try { localStorage.setItem('SECRET_BEST_DRIFT', String(g.score)); } catch (e) {}
          }
          stopGame();
        }
      }
    }

    for (let i = g.orbs.length - 1; i >= 0; i -= 1) {
      const o = g.orbs[i];
      o.y += o.vy * dt;
      if (o.y > h + 30) {
        g.orbs.splice(i, 1);
        continue;
      }
      const dx = ship.x - o.x;
      const dy = ship.y - o.y;
      if (Math.hypot(dx, dy) < 24) {
        g.orbs.splice(i, 1);
        g.score += 1;
        setScore(g.score);
        emitParticles(o.x, o.y, '#38f2ff', 12);
      }
    }

    for (let i = g.particles.length - 1; i >= 0; i -= 1) {
      const p = g.particles[i];
      p.life -= dt;
      if (p.life <= 0) {
        g.particles.splice(i, 1);
        continue;
      }
      p.x += p.vx * dt;
      p.y += p.vy * dt;
      p.vx *= 0.97;
      p.vy *= 0.97;
    }

    for (const o of g.orbs) {
      ctx.save();
      ctx.shadowColor = '#38f2ff';
      ctx.shadowBlur = 20;
      ctx.fillStyle = '#7df6ff';
      ctx.beginPath();
      ctx.arc(o.x, o.y, o.r, 0, Math.PI * 2);
      ctx.fill();
      ctx.restore();
    }

    for (const e of g.enemies) {
      ctx.save();
      ctx.translate(e.x, e.y);
      ctx.rotate(e.a);
      ctx.shadowColor = '#ff3b5f';
      ctx.shadowBlur = 24;
      ctx.fillStyle = '#ff2d4f';
      ctx.beginPath();
      for (let i = 0; i < 6; i += 1) {
        const ang = (Math.PI * 2 * i) / 6;
        const rad = i % 2 === 0 ? e.r : e.r * 0.6;
        const px = Math.cos(ang) * rad;
        const py = Math.sin(ang) * rad;
        if (i === 0) ctx.moveTo(px, py); else ctx.lineTo(px, py);
      }
      ctx.closePath();
      ctx.fill();
      ctx.restore();
    }

    ctx.save();
    ctx.translate(ship.x, ship.y);
    ctx.shadowColor = '#7d7bff';
    ctx.shadowBlur = 28;
    const grad = ctx.createRadialGradient(0, 0, 4, 0, 0, 28);
    grad.addColorStop(0, '#c6d1ff');
    grad.addColorStop(0.5, '#6b7dff');
    grad.addColorStop(1, '#2d2b5f');
    ctx.fillStyle = grad;
    ctx.beginPath();
    ctx.moveTo(0, -22);
    ctx.lineTo(18, 18);
    ctx.lineTo(0, 12);
    ctx.lineTo(-18, 18);
    ctx.closePath();
    ctx.fill();
    ctx.restore();

    for (const p of g.particles) {
      ctx.save();
      ctx.globalAlpha = Math.max(0, Math.min(1, p.life));
      ctx.shadowColor = p.color;
      ctx.shadowBlur = 16;
      ctx.fillStyle = p.color;
      ctx.beginPath();
      ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2);
      ctx.fill();
      ctx.restore();
    }

    rafRef.current = requestAnimationFrame(tick);
  };

  useEffect(() => {
    const resize = () => {
      const canvas = canvasRef.current;
      const wrap = wrapRef.current;
      if (!canvas || !wrap) return;
      const rect = wrap.getBoundingClientRect();
      const dpr = Math.min(2, window.devicePixelRatio || 1);
      canvas.width = rect.width * dpr;
      canvas.height = rect.height * dpr;
      canvas.style.width = `${rect.width}px`;
      canvas.style.height = `${rect.height}px`;
      const ctx = canvas.getContext('2d');
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      gameRef.current.stars = [];
    };
    resize();
    window.addEventListener('resize', resize);
    return () => window.removeEventListener('resize', resize);
  }, []);

  useEffect(() => {
    const wrap = wrapRef.current;
    if (!wrap) return () => {};
    const onMove = (e) => {
      const rect = wrap.getBoundingClientRect();
      pointerRef.current = {
        x: (e.clientX - rect.left) / rect.width,
        y: (e.clientY - rect.top) / rect.height,
        active: true,
      };
    };
    const onLeave = () => {
      pointerRef.current.active = false;
    };
    wrap.addEventListener('pointermove', onMove);
    wrap.addEventListener('pointerleave', onLeave);
    return () => {
      wrap.removeEventListener('pointermove', onMove);
      wrap.removeEventListener('pointerleave', onLeave);
    };
  }, []);

  useEffect(() => {
    return () => {
      runningRef.current = false;
      setIsRunning(false);
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, []);

  return (
    <GameShell
      title="Neon Drift"
      subtitle="Collect cyan orbs. Dodge red rifts. Move with mouse or touch."
    >
      <GameCanvas
        wrapRef={wrapRef}
        canvasRef={canvasRef}
        score={score}
        lives={lives}
        bestScore={bestScore}
        gameOver={gameOver}
        isRunning={isRunning}
        onStart={startGame}
        onPause={stopGame}
      />
    </GameShell>
  );
}

function OrbitHopperGame() {
  const canvasRef = useRef(null);
  const wrapRef = useRef(null);
  const rafRef = useRef(null);
  const lastRef = useRef(0);
  const keysRef = useRef({ jump: false });
  const runningRef = useRef(false);
  const gameRef = useRef({
    player: { x: 160, y: 0, vy: 0, r: 16, onGround: false },
    platforms: [],
    stars: [],
    score: 0,
    t: 0,
  });

  const [score, setScore] = useState(0);
  const [gameOver, setGameOver] = useState(false);
  const [isRunning, setIsRunning] = useState(false);
  const [bestScore, setBestScore] = useState(() => {
    try {
      const v = localStorage.getItem('SECRET_BEST_HOPPER');
      return v ? Number(v) || 0 : 0;
    } catch (e) {
      return 0;
    }
  });

  const resetGame = (w, h) => {
    const g = gameRef.current;
    g.player = { x: 160, y: h * 0.5, vy: 0, r: 16, onGround: false };
    g.platforms = [];
    g.stars = [];
    g.score = 0;
    g.t = 0;
    const baseY = h * 0.7;
    for (let i = 0; i < 6; i += 1) {
      g.platforms.push({
        x: i * 180,
        y: baseY - (i % 2) * 60,
        w: 140,
        h: 16,
      });
      g.stars.push({
        x: i * 180 + 70,
        y: baseY - (i % 2) * 60 - 40,
        r: 8,
        active: true,
      });
    }
    setScore(0);
    setGameOver(false);
  };

  const startGame = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    resetGame(canvas.width, canvas.height);
    runningRef.current = true;
    setIsRunning(true);
    lastRef.current = performance.now();
    rafRef.current = requestAnimationFrame(tick);
  };

  const stopGame = () => {
    runningRef.current = false;
    setIsRunning(false);
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
  };

  const tick = (now) => {
    if (!runningRef.current) return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    const w = canvas.width;
    const h = canvas.height;
    const dt = Math.min(0.05, (now - lastRef.current) / 1000);
    lastRef.current = now;
    const g = gameRef.current;

    const bg = ctx.createLinearGradient(0, 0, 0, h);
    bg.addColorStop(0, '#0a0b16');
    bg.addColorStop(1, '#06070f');
    ctx.fillStyle = bg;
    ctx.fillRect(0, 0, w, h);

    const speed = 220 + g.score * 4;
    const gravity = 1200;
    const player = g.player;

    player.vy += gravity * dt;
    player.y += player.vy * dt;
    player.onGround = false;

    for (const p of g.platforms) {
      p.x -= speed * dt;
    }
    for (const s of g.stars) {
      s.x -= speed * dt;
    }

    const lastPlatform = g.platforms[g.platforms.length - 1];
    if (lastPlatform && lastPlatform.x < w - 180) {
      const newY = (Math.random() * 0.4 + 0.4) * h;
      const newX = w + Math.random() * 120;
      g.platforms.push({ x: newX, y: newY, w: 150, h: 16 });
      g.stars.push({ x: newX + 80, y: newY - 40, r: 8, active: true });
    }

    while (g.platforms.length && g.platforms[0].x + g.platforms[0].w < -60) {
      g.platforms.shift();
    }
    while (g.stars.length && g.stars[0].x < -60) {
      g.stars.shift();
    }

    for (const p of g.platforms) {
      const withinX = player.x + player.r > p.x && player.x - player.r < p.x + p.w;
      const falling = player.vy > 0;
      if (withinX && falling && player.y + player.r >= p.y && player.y + player.r <= p.y + p.h + 12) {
        player.y = p.y - player.r;
        player.vy = 0;
        player.onGround = true;
      }
      ctx.fillStyle = 'rgba(120,150,255,0.3)';
      ctx.fillRect(p.x, p.y, p.w, p.h);
      ctx.fillStyle = 'rgba(120,150,255,0.8)';
      ctx.fillRect(p.x, p.y, p.w, 3);
    }

    if ((keysRef.current.jump || player.onGround) && player.onGround) {
      player.vy = -520;
      player.onGround = false;
      keysRef.current.jump = false;
    }

    for (const s of g.stars) {
      if (!s.active) continue;
      const dx = player.x - s.x;
      const dy = player.y - s.y;
      if (Math.hypot(dx, dy) < player.r + s.r + 6) {
        s.active = false;
        g.score += 1;
        setScore(g.score);
      }
      if (s.active) {
        ctx.save();
        ctx.shadowColor = '#ffe66d';
        ctx.shadowBlur = 18;
        ctx.fillStyle = '#fff3b0';
        ctx.beginPath();
        ctx.arc(s.x, s.y, s.r, 0, Math.PI * 2);
        ctx.fill();
        ctx.restore();
      }
    }

    ctx.save();
    ctx.shadowColor = '#8ef6ff';
    ctx.shadowBlur = 18;
    ctx.fillStyle = '#5de2ff';
    ctx.beginPath();
    ctx.arc(player.x, player.y, player.r, 0, Math.PI * 2);
    ctx.fill();
    ctx.restore();

    g.t += dt;
    if (player.y > h + 80) {
      setGameOver(true);
      setIsRunning(false);
      if (g.score > bestScore) {
        setBestScore(g.score);
        try { localStorage.setItem('SECRET_BEST_HOPPER', String(g.score)); } catch (e) {}
      }
      stopGame();
    }

    rafRef.current = requestAnimationFrame(tick);
  };

  useEffect(() => {
    const resize = () => {
      const canvas = canvasRef.current;
      const wrap = wrapRef.current;
      if (!canvas || !wrap) return;
      const rect = wrap.getBoundingClientRect();
      const dpr = Math.min(2, window.devicePixelRatio || 1);
      canvas.width = rect.width * dpr;
      canvas.height = rect.height * dpr;
      canvas.style.width = `${rect.width}px`;
      canvas.style.height = `${rect.height}px`;
      const ctx = canvas.getContext('2d');
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };
    resize();
    window.addEventListener('resize', resize);
    return () => window.removeEventListener('resize', resize);
  }, []);

  useEffect(() => {
    const onKeyDown = (e) => {
      if (e.code === 'Space') keysRef.current.jump = true;
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, []);

  useEffect(() => {
    return () => {
      runningRef.current = false;
      setIsRunning(false);
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, []);

  return (
    <GameShell
      title="Orbit Hopper"
      subtitle="Auto-runner. Tap Space or click to jump and collect stars."
    >
      <GameCanvas
        wrapRef={wrapRef}
        canvasRef={canvasRef}
        score={score}
        lives={null}
        bestScore={bestScore}
        gameOver={gameOver}
        isRunning={isRunning}
        onStart={startGame}
        onPause={stopGame}
        onClick={() => { keysRef.current.jump = true; }}
      />
    </GameShell>
  );
}

function NeonFPSGame() {
  const canvasRef = useRef(null);
  const wrapRef = useRef(null);
  const rafRef = useRef(null);
  const lastRef = useRef(0);
  const keysRef = useRef({});
  const runningRef = useRef(false);
  const gameRef = useRef({
    x: 3.5,
    y: 3.5,
    dir: 0,
    score: 0,
    t: 0,
  });

  const [score, setScore] = useState(0);
  const [isRunning, setIsRunning] = useState(false);

  const map = [
    '1111111111111111',
    '1000000000100001',
    '1011110110101101',
    '1000010000100001',
    '1011011110101111',
    '1001000010000001',
    '1011010110111101',
    '1000010000100001',
    '1011110110101101',
    '1000000000000001',
    '1011110110111101',
    '1000010000100001',
    '1011011110101111',
    '1000000000100001',
    '1000000000000001',
    '1111111111111111',
  ];
  const mapW = 16;
  const mapH = 16;

  const resetGame = () => {
    const g = gameRef.current;
    g.x = 3.5;
    g.y = 3.5;
    g.dir = 0;
    g.score = 0;
    g.t = 0;
    setScore(0);
  };

  const startGame = () => {
    resetGame();
    runningRef.current = true;
    setIsRunning(true);
    lastRef.current = performance.now();
    rafRef.current = requestAnimationFrame(tick);
  };

  const stopGame = () => {
    runningRef.current = false;
    setIsRunning(false);
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
  };

  const isWall = (x, y) => {
    const mx = Math.floor(x);
    const my = Math.floor(y);
    if (mx < 0 || my < 0 || mx >= mapW || my >= mapH) return true;
    return map[my][mx] === '1';
  };

  const tick = (now) => {
    if (!runningRef.current) return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    const w = canvas.width;
    const h = canvas.height;
    const dt = Math.min(0.05, (now - lastRef.current) / 1000);
    lastRef.current = now;
    const g = gameRef.current;

    const moveSpeed = 2.6;
    const rotSpeed = 2.2;
    const dx = Math.cos(g.dir);
    const dy = Math.sin(g.dir);

    if (keysRef.current.KeyW) {
      const nx = g.x + dx * moveSpeed * dt;
      const ny = g.y + dy * moveSpeed * dt;
      if (!isWall(nx, g.y)) g.x = nx;
      if (!isWall(g.x, ny)) g.y = ny;
    }
    if (keysRef.current.KeyS) {
      const nx = g.x - dx * moveSpeed * dt;
      const ny = g.y - dy * moveSpeed * dt;
      if (!isWall(nx, g.y)) g.x = nx;
      if (!isWall(g.x, ny)) g.y = ny;
    }
    if (keysRef.current.KeyQ) {
      const nx = g.x - dy * moveSpeed * dt;
      const ny = g.y + dx * moveSpeed * dt;
      if (!isWall(nx, g.y)) g.x = nx;
      if (!isWall(g.x, ny)) g.y = ny;
    }
    if (keysRef.current.KeyE) {
      const nx = g.x + dy * moveSpeed * dt;
      const ny = g.y - dx * moveSpeed * dt;
      if (!isWall(nx, g.y)) g.x = nx;
      if (!isWall(g.x, ny)) g.y = ny;
    }
    if (keysRef.current.KeyA || keysRef.current.ArrowLeft) g.dir -= rotSpeed * dt;
    if (keysRef.current.KeyD || keysRef.current.ArrowRight) g.dir += rotSpeed * dt;

    g.t += dt;
    g.score += dt * 10;
    setScore(Math.floor(g.score));

    const sky = ctx.createLinearGradient(0, 0, 0, h * 0.6);
    sky.addColorStop(0, '#0b1238');
    sky.addColorStop(1, '#0b0f25');
    ctx.fillStyle = sky;
    ctx.fillRect(0, 0, w, h * 0.5);

    const floor = ctx.createLinearGradient(0, h * 0.5, 0, h);
    floor.addColorStop(0, '#0a0a12');
    floor.addColorStop(1, '#050507');
    ctx.fillStyle = floor;
    ctx.fillRect(0, h * 0.5, w, h * 0.5);

    const fov = Math.PI / 3;
    for (let x = 0; x < w; x += 1) {
      const cameraX = (2 * x) / w - 1;
      const rayDirX = Math.cos(g.dir) + Math.cos(g.dir + Math.PI / 2) * cameraX * Math.tan(fov / 2);
      const rayDirY = Math.sin(g.dir) + Math.sin(g.dir + Math.PI / 2) * cameraX * Math.tan(fov / 2);

      let mapX = Math.floor(g.x);
      let mapY = Math.floor(g.y);
      const deltaDistX = Math.abs(1 / (rayDirX || 0.0001));
      const deltaDistY = Math.abs(1 / (rayDirY || 0.0001));
      let sideDistX;
      let sideDistY;
      let stepX;
      let stepY;
      let side = 0;

      if (rayDirX < 0) {
        stepX = -1;
        sideDistX = (g.x - mapX) * deltaDistX;
      } else {
        stepX = 1;
        sideDistX = (mapX + 1.0 - g.x) * deltaDistX;
      }
      if (rayDirY < 0) {
        stepY = -1;
        sideDistY = (g.y - mapY) * deltaDistY;
      } else {
        stepY = 1;
        sideDistY = (mapY + 1.0 - g.y) * deltaDistY;
      }

      let hit = false;
      let safety = 0;
      while (!hit && safety < 128) {
        safety += 1;
        if (sideDistX < sideDistY) {
          sideDistX += deltaDistX;
          mapX += stepX;
          side = 0;
        } else {
          sideDistY += deltaDistY;
          mapY += stepY;
          side = 1;
        }
        if (mapY < 0 || mapY >= mapH || mapX < 0 || mapX >= mapW) {
          hit = true;
          break;
        }
        if (map[mapY][mapX] === '1') hit = true;
      }

      const perpWallDist = side === 0 ? (sideDistX - deltaDistX) : (sideDistY - deltaDistY);
      const lineHeight = Math.min(h, h / Math.max(0.1, perpWallDist));
      const drawStart = Math.max(0, (h - lineHeight) / 2);
      const shade = side === 1 ? 0.7 : 1;
      const fog = Math.max(0, 1 - perpWallDist / 9);
      const r = Math.floor(120 * shade * fog + 30);
      const gcol = Math.floor(80 * shade * fog + 20);
      const b = Math.floor(220 * shade * fog + 40);
      ctx.strokeStyle = `rgb(${r},${gcol},${b})`;
      ctx.beginPath();
      ctx.moveTo(x + 0.5, drawStart);
      ctx.lineTo(x + 0.5, drawStart + lineHeight);
      ctx.stroke();
    }

    ctx.save();
    ctx.strokeStyle = 'rgba(255,255,255,0.6)';
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.moveTo(w / 2 - 8, h / 2);
    ctx.lineTo(w / 2 + 8, h / 2);
    ctx.moveTo(w / 2, h / 2 - 8);
    ctx.lineTo(w / 2, h / 2 + 8);
    ctx.stroke();
    ctx.restore();

    rafRef.current = requestAnimationFrame(tick);
  };

  useEffect(() => {
    const resize = () => {
      const canvas = canvasRef.current;
      const wrap = wrapRef.current;
      if (!canvas || !wrap) return;
      const rect = wrap.getBoundingClientRect();
      const dpr = Math.min(2, window.devicePixelRatio || 1);
      canvas.width = rect.width * dpr;
      canvas.height = rect.height * dpr;
      canvas.style.width = `${rect.width}px`;
      canvas.style.height = `${rect.height}px`;
      const ctx = canvas.getContext('2d');
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };
    resize();
    window.addEventListener('resize', resize);
    return () => window.removeEventListener('resize', resize);
  }, []);

  useEffect(() => {
    const onKeyDown = (e) => {
      keysRef.current[e.code] = true;
    };
    const onKeyUp = (e) => {
      keysRef.current[e.code] = false;
    };
    window.addEventListener('keydown', onKeyDown);
    window.addEventListener('keyup', onKeyUp);
    return () => {
      window.removeEventListener('keydown', onKeyDown);
      window.removeEventListener('keyup', onKeyUp);
    };
  }, []);

  useEffect(() => {
    return () => {
      runningRef.current = false;
      setIsRunning(false);
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, []);

  return (
    <GameShell
      title="Neon FPS"
      subtitle="WASD to move. Q/E strafe. Arrow keys or A/D rotate. FPS-style maze."
    >
      <GameCanvas
        wrapRef={wrapRef}
        canvasRef={canvasRef}
        score={score}
        lives={null}
        bestScore={null}
        gameOver={false}
        isRunning={isRunning}
        onStart={startGame}
        onPause={stopGame}
      />
    </GameShell>
  );
}

function GameCanvas({
  wrapRef,
  canvasRef,
  score,
  lives,
  bestScore,
  gameOver,
  isRunning,
  onStart,
  onPause,
  onClick,
}) {
  return (
    <div
      ref={wrapRef}
      onClick={onClick}
      role="presentation"
      style={{
        position: 'relative',
        width: '100%',
        height: '62vh',
        minHeight: 420,
        borderRadius: 16,
        overflow: 'hidden',
        background: 'rgba(10,12,24,0.8)',
        border: '1px solid rgba(120,140,255,0.25)',
        boxShadow: '0 20px 80px rgba(0,0,0,0.55), inset 0 0 40px rgba(120,140,255,0.15)',
      }}
    >
      <canvas ref={canvasRef} style={{ display: 'block' }} />

      <div
        style={{
          position: 'absolute',
          top: 12,
          left: 12,
          display: 'flex',
          gap: 10,
          alignItems: 'center',
          padding: '8px 12px',
          background: 'rgba(7,10,18,0.65)',
          borderRadius: 12,
          border: '1px solid rgba(120,140,255,0.25)',
          backdropFilter: 'blur(6px)',
          flexWrap: 'wrap',
        }}
      >
        <strong>Score:</strong> {score}
        {typeof lives === 'number' ? (
          <>
            <span style={{ opacity: 0.7 }}>•</span>
            <strong>Lives:</strong> {lives}
          </>
        ) : null}
        {typeof bestScore === 'number' ? (
          <>
            <span style={{ opacity: 0.7 }}>•</span>
            <strong>Best:</strong> {bestScore}
          </>
        ) : null}
      </div>

      <div
        style={{
          position: 'absolute',
          bottom: 16,
          left: 16,
          right: 16,
          display: 'flex',
          justifyContent: 'flex-end',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: 12,
        }}
      >
        {!isRunning && !gameOver ? (
          <button className="btn-glass-primary" type="button" onClick={onStart}>Start</button>
        ) : null}
        {gameOver ? (
          <button className="btn-glass-primary" type="button" onClick={onStart}>Restart</button>
        ) : isRunning ? (
          <button className="btn-glass-secondary" type="button" onClick={onPause}>Pause</button>
        ) : null}
      </div>

      {gameOver ? (
        <div
          style={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: 'rgba(5,8,18,0.7)',
            textAlign: 'center',
            backdropFilter: 'blur(8px)',
          }}
        >
          <div>
            <div style={{ fontSize: 28, fontWeight: 700, marginBottom: 8 }}>Run Complete</div>
            <div className="meta-text">Score: {score}</div>
          </div>
        </div>
      ) : null}
    </div>
  );
    if (!ship.x && !ship.y) {
      ship.x = targetX;
      ship.y = targetY;
    }

    const difficulty = 1 + Math.min(2, g.score / 20);

    if (g.t > g.spawnEnemyAt) {
      g.spawnEnemyAt = g.t + (0.65 - Math.min(0.35, g.score * 0.01));
      g.enemies.push({
        x: Math.random() * (w - 80) + 40,
        y: -30,
        r: Math.random() * 18 + 16,
        vy: Math.random() * 140 + 120 * difficulty,
        vx: (Math.random() - 0.5) * 60,
        spin: (Math.random() - 0.5) * 3,
        a: Math.random() * Math.PI * 2,
      });
    }

    if (g.t > g.spawnOrbAt) {
      g.spawnOrbAt = g.t + 1.1 + Math.random() * 0.6;
      g.orbs.push({
        x: Math.random() * (w - 120) + 60,
        y: -20,
        r: 10,
        vy: Math.random() * 120 + 90,
      });
    }

    // Enemies
    for (let i = g.enemies.length - 1; i >= 0; i -= 1) {
      const e = g.enemies[i];
      e.y += e.vy * dt;
      e.x += e.vx * dt;
      e.a += e.spin * dt;
      if (e.y > h + 60) {
        g.enemies.splice(i, 1);
        continue;
      }
      const dx = ship.x - e.x;
      const dy = ship.y - e.y;
      const dist = Math.hypot(dx, dy);
      if (dist < e.r + 18) {
        g.enemies.splice(i, 1);
        g.lives -= 1;
        setLives(g.lives);
        emitParticles(e.x, e.y, '#ff3b5f');
        if (g.lives <= 0) {
          setGameOver(true);
          setIsRunning(false);
          if (g.score > bestScore) {
            setBestScore(g.score);
            try { localStorage.setItem('SECRET_BEST', String(g.score)); } catch (e) {}
          }
          stopGame();
        }
      }
    }

    // Orbs
    for (let i = g.orbs.length - 1; i >= 0; i -= 1) {
      const o = g.orbs[i];
      o.y += o.vy * dt;
      if (o.y > h + 30) {
        g.orbs.splice(i, 1);
        continue;
      }
      const dx = ship.x - o.x;
      const dy = ship.y - o.y;
      if (Math.hypot(dx, dy) < 24) {
        g.orbs.splice(i, 1);
        g.score += 1;
        setScore(g.score);
        emitParticles(o.x, o.y, '#38f2ff', 12);
      }
    }

    // Particles
    for (let i = g.particles.length - 1; i >= 0; i -= 1) {
      const p = g.particles[i];
      p.life -= dt;
      if (p.life <= 0) {
        g.particles.splice(i, 1);
        continue;
      }
      p.x += p.vx * dt;
      p.y += p.vy * dt;
      p.vx *= 0.97;
      p.vy *= 0.97;
    }

    // Draw orbs
    for (const o of g.orbs) {
      ctx.save();
      ctx.shadowColor = '#38f2ff';
      ctx.shadowBlur = 20;
      ctx.fillStyle = '#7df6ff';
      ctx.beginPath();
      ctx.arc(o.x, o.y, o.r, 0, Math.PI * 2);
      ctx.fill();
      ctx.restore();
    }

    // Draw enemies
    for (const e of g.enemies) {
      ctx.save();
      ctx.translate(e.x, e.y);
      ctx.rotate(e.a);
      ctx.shadowColor = '#ff3b5f';
      ctx.shadowBlur = 24;
      ctx.fillStyle = '#ff2d4f';
      ctx.beginPath();
      for (let i = 0; i < 6; i += 1) {
        const ang = (Math.PI * 2 * i) / 6;
        const rad = i % 2 === 0 ? e.r : e.r * 0.6;
        const px = Math.cos(ang) * rad;
        const py = Math.sin(ang) * rad;
        if (i === 0) ctx.moveTo(px, py); else ctx.lineTo(px, py);
      }
      ctx.closePath();
      ctx.fill();
      ctx.restore();
    }

    // Draw ship
    ctx.save();
    ctx.translate(ship.x, ship.y);
    ctx.shadowColor = '#7d7bff';
    ctx.shadowBlur = 28;
    const grad = ctx.createRadialGradient(0, 0, 4, 0, 0, 28);
    grad.addColorStop(0, '#c6d1ff');
    grad.addColorStop(0.5, '#6b7dff');
    grad.addColorStop(1, '#2d2b5f');
    ctx.fillStyle = grad;
    ctx.beginPath();
    ctx.moveTo(0, -22);
    ctx.lineTo(18, 18);
    ctx.lineTo(0, 12);
    ctx.lineTo(-18, 18);
    ctx.closePath();
    ctx.fill();
    ctx.restore();

    // Draw particles
    for (const p of g.particles) {
      ctx.save();
      ctx.globalAlpha = Math.max(0, Math.min(1, p.life));
      ctx.shadowColor = p.color;
      ctx.shadowBlur = 16;
      ctx.fillStyle = p.color;
      ctx.beginPath();
      ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2);
      ctx.fill();
      ctx.restore();
    }

    rafRef.current = requestAnimationFrame(tick);
  };

  useEffect(() => {
    if (!isAdmin) return () => {};
    const resize = () => {
      const canvas = canvasRef.current;
      const wrap = wrapRef.current;
      if (!canvas || !wrap) return;
      const rect = wrap.getBoundingClientRect();
      const dpr = Math.min(2, window.devicePixelRatio || 1);
      canvas.width = rect.width * dpr;
      canvas.height = rect.height * dpr;
      canvas.style.width = `${rect.width}px`;
      canvas.style.height = `${rect.height}px`;
      const ctx = canvas.getContext('2d');
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      gameRef.current.stars = [];
    };
    resize();
    window.addEventListener('resize', resize);
    return () => window.removeEventListener('resize', resize);
  }, [isAdmin]);

  useEffect(() => {
    if (!isAdmin) return () => {};
    const wrap = wrapRef.current;
    if (!wrap) return () => {};
    const onMove = (e) => {
      const rect = wrap.getBoundingClientRect();
      pointerRef.current = {
        x: (e.clientX - rect.left) / rect.width,
        y: (e.clientY - rect.top) / rect.height,
        active: true,
      };
    };
    const onLeave = () => {
      pointerRef.current.active = false;
    };
    wrap.addEventListener('pointermove', onMove);
    wrap.addEventListener('pointerleave', onLeave);
    return () => {
      wrap.removeEventListener('pointermove', onMove);
      wrap.removeEventListener('pointerleave', onLeave);
    };
  }, [isAdmin]);

  useEffect(() => {
    return () => stopGame();
  }, []);

  if (loading) {
    return <div style={{ paddingTop: '4rem', textAlign: 'center' }}>Loading…</div>;
  }

  if (!isAdmin) {
    return (
      <div className="page-section">
        <div className="page-container" style={{ maxWidth: 1100 }}>
          <div className="glass-card" style={{ padding: 18, textAlign: 'center' }}>
            Not Found
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="page-section">
      <div className="page-container" style={{ maxWidth: 1100 }}>
        <h1 className="title" style={{ textAlign: 'center', marginTop: 0 }}>Secret</h1>
        <p className="subtitle" style={{ textAlign: 'center' }}>
          Neon Drift — collect orbs, dodge rifts.
        </p>

        <div className="glass-card" style={{ padding: 16 }}>
          <div
            ref={wrapRef}
            style={{
              position: 'relative',
              width: '100%',
              height: '62vh',
              minHeight: 420,
              borderRadius: 16,
              overflow: 'hidden',
              background: 'rgba(10,12,24,0.8)',
              border: '1px solid rgba(120,140,255,0.25)',
              boxShadow: '0 20px 80px rgba(0,0,0,0.55), inset 0 0 40px rgba(120,140,255,0.15)',
            }}
          >
            <canvas ref={canvasRef} style={{ display: 'block' }} />

            <div
              style={{
                position: 'absolute',
                top: 12,
                left: 12,
                display: 'flex',
                gap: 10,
                alignItems: 'center',
                padding: '8px 12px',
                background: 'rgba(7,10,18,0.65)',
                borderRadius: 12,
                border: '1px solid rgba(120,140,255,0.25)',
                backdropFilter: 'blur(6px)',
              }}
            >
              <strong>Score:</strong> {score}
              <span style={{ opacity: 0.7 }}>•</span>
              <strong>Lives:</strong> {lives}
              <span style={{ opacity: 0.7 }}>•</span>
              <strong>Best:</strong> {bestScore}
            </div>

            <div
              style={{
                position: 'absolute',
                bottom: 16,
                left: 16,
                right: 16,
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                flexWrap: 'wrap',
                gap: 12,
              }}
            >
              <div style={{ opacity: 0.85 }}>
                Move with mouse or touch. Collect cyan orbs. Dodge red rifts.
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                {!isRunning && !gameOver ? (
                  <button className="btn-glass-primary" type="button" onClick={startGame}>Start</button>
                ) : null}
                {gameOver ? (
                  <button className="btn-glass-primary" type="button" onClick={startGame}>Restart</button>
                ) : isRunning ? (
                  <button className="btn-glass-secondary" type="button" onClick={stopGame}>
                    Pause
                  </button>
                ) : null}
              </div>
            </div>

            {gameOver ? (
              <div
                style={{
                  position: 'absolute',
                  inset: 0,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  background: 'rgba(5,8,18,0.7)',
                  textAlign: 'center',
                  backdropFilter: 'blur(8px)',
                }}
              >
                <div>
                  <div style={{ fontSize: 28, fontWeight: 700, marginBottom: 8 }}>Run Complete</div>
                  <div className="meta-text">Score: {score}</div>
                </div>
              </div>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
*/
